import {getFunctions, httpsCallable} from "firebase/functions";
import {app} from "../../firebase";
import {withLegacyReceiptFallbacks} from "../../utils/accountingEntries";
import {escapeHtml as escapePrintValue} from "../../utils/escapeHtml";
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  runTransaction,
  writeBatch,
} from "firebase/firestore";

import { db } from "../../firebase";

import {
  getBusinessDateKey,
  getBusinessTime,
} from "../../utils/businessDate";

import {
  calculateBill,
  calculatePaidTotal,
  financialFields,
  formatMoney,
  roundMoney,
  toAmount,
} from "../../services/billingService";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const lower = (value) =>
  clean(value).toLowerCase();

const getDateObject = (value) => {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    return value.toDate();
  }

  if (value?.seconds) {
    return new Date(value.seconds * 1000);
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? null
    : date;
};

const getTimestamp = (value) =>
  getDateObject(value)?.getTime() || 0;

const formatDateTime = (value) => {
  const date = getDateObject(value);

  if (!date) return "-";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTime = (value) => {
  const date = getDateObject(value);

  if (!date) return "-";

  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPreviousBusinessDate = (
  businessDate
) => {
  const date = new Date(
    `${businessDate}T12:00:00`
  );

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  date.setDate(date.getDate() - 1);

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(
      2,
      "0"
    ),
    String(date.getDate()).padStart(
      2,
      "0"
    ),
  ].join("-");
};

const getNextBusinessDate = (
  businessDate
) => {
  const date = new Date(
    `${businessDate}T12:00:00`
  );

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  date.setDate(date.getDate() + 1);

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(
      2,
      "0"
    ),
    String(date.getDate()).padStart(
      2,
      "0"
    ),
  ].join("-");
};

const getPaymentModeKey = (value) => {
  const mode = lower(value);

  if (mode === "cash") return "cash";
  if (mode === "upi") return "upi";
  if (mode === "card") return "card";

  if (
    mode.includes("bank") ||
    mode.includes("transfer")
  ) {
    return "bank";
  }

  if (
    mode.includes("fab") ||
    mode.includes("fabhotel")
  ) {
    return "fab";
  }

  if (
    mode.includes("brevistay") ||
    mode.includes("brevistay")
  ) {
    return "brevistay";
  }

  if (
    mode.includes("booking") ||
    mode.includes("agoda") ||
    mode.includes("goibibo") ||
    mode.includes("makemytrip") ||
    mode.includes("ota")
  ) {
    return "ota";
  }

  return "other";
};

const isCashMode = (value) =>
  getPaymentModeKey(value) === "cash";

const isExpenseEntry = (entry) =>
  entry?.entryType === "expense";

const isCashHandoverEntry = (entry) =>
  entry?.entryType === "cash_handover" ||
  entry?.category === "cash_handover" ||
  entry?.source === "cash_handover";

const isRevenueEntry = (entry) =>
  entry?.entryType === "revenue";

const isPaymentEntry = (entry) =>
  entry?.entryType === "payment" ||
  entry?.entryType === "advance" ||
  entry?.category ===
    "booking_diary_advance";

const isAdvanceEntry = (entry) =>
  entry?.category === "advance" ||
  entry?.source === "advance_booking" ||
  entry?.source === "pending_received" ||
  entry?.source === "early_check_in" ||
  entry?.source === "late_checkout" ||
  entry?.source === "extra_mattress" ||
  lower(entry?.title).includes("advance") ||
  lower(entry?.title).includes(
    "pending amount"
  ) ||
  lower(entry?.title).includes(
    "early check"
  ) ||
  lower(entry?.title).includes(
    "late check"
  );

const isOnlineEntry = (entry) =>
  !isCashMode(entry?.paymentMode) &&
  Boolean(clean(entry?.paymentMode));

const isMainRoomRegisterEntry = (
  entry
) =>
  (
    entry?.entryType ===
      "revenue" &&
    entry?.category === "room"
  ) ||
  entry?.entryType ===
    "manual_register" ||
  entry?.category ===
    "manual_register";

const getEntryAmount = (entry) =>
  Math.max(0, toAmount(entry?.amount));

const getEntrySerial = (
  entry,
  index
) =>
  clean(
    entry?.serialNumber ||
      entry?.adRegisterSerialNo ||
      entry?.registerSerialNo
  ) || String(index + 1);

const getEntryGuestName = (entry) =>
  clean(
    entry?.guestName ||
      entry?.name
  ) || "-";

const getEntryTransactionReference = (
  entry
) =>
  clean(
    entry?.transactionReference ||
      entry?.transactionId ||
      entry?.paymentReference
  );


const getRegisterRow = (
  entry,
  index = 0
) => {
  const amount =
    getEntryAmount(entry);

  const mode =
    getPaymentModeKey(
      entry?.paymentMode
    );

  const hasOwn = (key) =>
    Object.prototype.hasOwnProperty.call(
      entry || {},
      key
    );

  const numberValue = (
    key,
    fallback
  ) =>
    hasOwn(key)
      ? Math.max(
          0,
          toAmount(entry?.[key])
        )
      : Math.max(
          0,
          toAmount(fallback)
        );

  const textValue = (
    key,
    fallback = ""
  ) =>
    hasOwn(key)
      ? clean(entry?.[key])
      : clean(fallback);

  const tariffFallback =
    isRevenueEntry(entry) &&
    entry?.category === "room"
      ? amount
      : 0;

  const cashFallback =
    isPaymentEntry(entry) &&
    mode === "cash"
      ? amount
      : 0;

  const onlineFallback =
    isPaymentEntry(entry) &&
    ["upi", "card", "bank", "ota", "other"].includes(
      mode
    )
      ? amount
      : 0;

  const fabFallback =
    isPaymentEntry(entry) &&
    mode === "fab"
      ? amount
      : 0;

  const brevistayFallback =
    isPaymentEntry(entry) &&
    mode === "brevistay"
      ? amount
      : 0;

  const foodFallback =
    isRevenueEntry(entry) &&
    entry?.category === "food"
      ? amount
      : 0;

  const advanceFallback =
    isAdvanceEntry(entry)
      ? amount
      : 0;

  const expenseFallback =
    isExpenseEntry(entry) ||
    isCashHandoverEntry(entry)
      ? amount
      : 0;

  const checkInFallback =
    entry?.checkInTime ||
    (entry?.category === "room"
      ? formatTime(
          entry?.eventAt ||
            entry?.createdAt
        )
      : "");

  const noteFallback =
    entry?.category ===
      "food_bill"
      ? clean(entry?.note) ||
        `Food Bill ${formatMoney(
          amount
        )} received from ${
          entry?.room || "-"
        }`
      : clean(entry?.note) ||
        (isAdvanceEntry(entry) ||
        isExpenseEntry(entry) ||
        isCashHandoverEntry(entry)
          ? clean(entry?.title)
          : "");

  return {
    serial:
      textValue(
        "ledgerSerial",
        getEntrySerial(
          entry,
          index
        )
      ) || String(index + 1),

    room:
      textValue(
        "ledgerRoom",
        entry?.room
      ),

    tariff:
      numberValue(
        "ledgerTariff",
        tariffFallback
      ),

    cash:
      numberValue(
        "ledgerCash",
        cashFallback
      ),

    online:
      numberValue(
        "ledgerOnline",
        onlineFallback
      ),

    fab:
      numberValue(
        "ledgerFab",
        fabFallback
      ),

    brevistay:
      numberValue(
        "ledgerBrevistay",
        brevistayFallback
      ),

    cashLabel:
      textValue(
        "ledgerCashLabel",
        ""
      ),

    onlineLabel:
      textValue(
        "ledgerOnlineLabel",
        ""
      ),

    fabLabel:
      textValue(
        "ledgerFabLabel",
        ""
      ),

    brevistayLabel:
      textValue(
        "ledgerBrevistayLabel",
        ""
      ),

    checkIn:
      textValue(
        "ledgerCheckIn",
        checkInFallback
      ),

    checkOut:
      textValue(
        "ledgerCheckOut",
        entry?.checkOutTime
      ),

    balance:
      numberValue(
        "ledgerBalance",
        entry?.balanceAmount
      ),

    guestName:
      textValue(
        "ledgerGuestName",
        getEntryGuestName(entry) === "-"
          ? ""
          : getEntryGuestName(entry)
      ),

    food:
      numberValue(
        "ledgerFoodBill",
        foodFallback
      ),

    advance:
      numberValue(
        "ledgerAdvance",
        advanceFallback
      ),

    expense:
      numberValue(
        "ledgerExpense",
        expenseFallback
      ),

    note:
      textValue(
        "ledgerNote",
        noteFallback
      ),

    transactionReference:
      textValue(
        "ledgerTransactionReference",
        getEntryTransactionReference(
          entry
        )
      ),
  };
};


const getLedgerPaymentLabel = (
  entry
) => {
  const mode =
    clean(entry?.paymentMode) ||
    "Cash";

  const code =
    getEntryTransactionReference(
      entry
    );

  return {
    mode,
    code,
  };
};

const isFoodReceiptEntry = (
  entry
) =>
  entry?.category ===
    "food_bill" ||
  entry?.entryType ===
    "food_payment" ||
  entry?.type ===
    "food_payment" ||
  clean(entry?.title)
    .toLowerCase()
    .includes("food bill received");

const isAdvancePendingReceiptEntry = (
  entry
) => {
  if (isAdvanceEntry(entry)) {
    return true;
  }

  const haystack = [
    entry?.title,
    entry?.note,
    entry?.category,
    entry?.entryType,
  ]
    .map((value) =>
      clean(value).toLowerCase()
    )
    .join(" ");

  return (
    haystack.includes(
      "pending amount received"
    ) ||
    haystack.includes(
      "pending received"
    ) ||
    haystack.includes(
      "advance received"
    ) ||
    haystack.includes(
      "advance amount received"
    )
  );
};

const buildSideLedgerLine = (
  entry
) => {
  const amount =
    getEntryAmount(entry);

  const room =
    clean(
      entry?.room ||
        entry?.ledgerRoom
    ) || "-";

  const {
    mode,
    code,
  } =
    getLedgerPaymentLabel(
      entry
    );

  return {
    id:
      entry?.id ||
      `${room}-${amount}-${entry?.eventAt || ""}`,

    amount,

    room,

    mode,

    code,

    guestName:
      clean(
        entry?.guestName ||
          entry?.ledgerGuestName
      ),

    note:
      clean(
        entry?.note ||
          entry?.ledgerNote ||
          entry?.title
      ),

    time:
      formatTime(
        entry?.eventAt ||
          entry?.createdAt
      ),

    title:
      clean(entry?.title),

    category:
      clean(entry?.category),

    eventAt:
      entry?.eventAt,

    originalEntry:
      entry,
  };
};

const createEmptyRegisterRow = () => ({
  serial: "",
  room: "",
  tariff: "",
  cash: "",
  online: "",
  fab: "",
  brevistay: "",
  checkIn: "",
  checkOut: "",
  balance: "",
  guestName: "",
  food: "",
  advance: "",
  expense: "",
  note: "",
  transactionReference: "",
});

const registerRowToFirestore = (
  row
) => ({
  ledgerSerial:
    clean(row.serial),

  ledgerRoom:
    clean(row.room).toUpperCase(),

  ledgerTariff:
    Math.max(
      0,
      toAmount(row.tariff)
    ),

  ledgerCash:
    Math.max(
      0,
      toAmount(row.cash)
    ),

  ledgerOnline:
    Math.max(
      0,
      toAmount(row.online)
    ),

  ledgerFab:
    Math.max(
      0,
      toAmount(row.fab)
    ),

  ledgerBrevistay:
    Math.max(
      0,
      toAmount(row.brevistay)
    ),

  ledgerCheckIn:
    clean(row.checkIn),

  ledgerCheckOut:
    clean(row.checkOut),

  ledgerBalance:
    Math.max(
      0,
      toAmount(row.balance)
    ),

  ledgerGuestName:
    clean(row.guestName),

  ledgerFoodBill:
    Math.max(
      0,
      toAmount(row.food)
    ),

  ledgerAdvance:
    Math.max(
      0,
      toAmount(row.advance)
    ),

  ledgerExpense:
    Math.max(
      0,
      toAmount(row.expense)
    ),

  ledgerNote:
    clean(row.note),

  ledgerTransactionReference:
    clean(
      row.transactionReference
    ),

  ledgerEditedAt:
    serverTimestamp(),
});

const createSummaryId = (
  hotelId,
  businessDate
) =>
  `${hotelId}_${businessDate}`;

/* -------------------------------------------------------------------------- */
/*                              Initial Forms                                 */
/* -------------------------------------------------------------------------- */

const createExpenseForm = () => ({
  title: "",
  amount: "",
  paymentMode: "Cash",
  category: "Misc",
  transactionReference: "",
  note: "",
});

const createManualReceiptForm = () => ({
  title: "",
  amount: "",
  paymentMode: "Cash",
  category: "advance",
  room: "",
  guestName: "",
  transactionReference: "",
  note: "",
});

const createCashHandoverForm = () => ({
  amount: "",
  receivedBy: "",
  note: "",
});

/* -------------------------------------------------------------------------- */
/*                             Day Book Panel                                 */
/* -------------------------------------------------------------------------- */

function DayBookPanel({
  hotelId,
  hotelName,
}) {
  const [summaryReady, setSummaryReady] = useState(false);
  const [openingReady, setOpeningReady] = useState(false);
  const [
    businessDate,
    setBusinessDate,
  ] = useState(
    getBusinessDateKey(new Date())
  );

  const [entries, setEntries] =
    useState([]);

  const [summary, setSummary] =
    useState({});

  const [
    openingCashInput,
    setOpeningCashInput,
  ] = useState("");

  const [
    openingCashSource,
    setOpeningCashSource,
  ] = useState("");

  const [
    expenseForm,
    setExpenseForm,
  ] = useState(
    createExpenseForm()
  );

  const [
    receiptForm,
    setReceiptForm,
  ] = useState(
    createManualReceiptForm()
  );

  const [
    cashHandoverForm,
    setCashHandoverForm,
  ] = useState(
    createCashHandoverForm()
  );

  const [search, setSearch] =
    useState("");

  const [
    entryTypeFilter,
    setEntryTypeFilter,
  ] = useState("all");

  const [
    paymentFilter,
    setPaymentFilter,
  ] = useState("all");

  const [
    activeForm,
    setActiveForm,
  ] = useState("expense");

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [loadError, setLoadError] =
    useState("");

  const [
    printCompact,
    setPrintCompact,
  ] = useState(false);

  const [
    editingEntryId,
    setEditingEntryId,
  ] = useState("");

  const [
    editRegisterRow,
    setEditRegisterRow,
  ] = useState(
    createEmptyRegisterRow()
  );

  const [
    manualRowOpen,
    setManualRowOpen,
  ] = useState(false);

  const [
    manualRegisterRow,
    setManualRegisterRow,
  ] = useState(
    createEmptyRegisterRow()
  );

  const [
    advanceManualFormOpen,
    setAdvanceManualFormOpen,
  ] = useState(false);

  const [
    advanceManualSaving,
    setAdvanceManualSaving,
  ] = useState(false);

  const [
    advanceManualEditId,
    setAdvanceManualEditId,
  ] = useState("");

  const [
    advanceManualForm,
    setAdvanceManualForm,
  ] = useState({
    amount: "",
    room: "",
    receiptType: "Advance Received",
    paymentMode: "Cash",
    transactionReference: "",
    guestName: "",
    note: "",
    time: "",
  });

  /* ------------------------------------------------------------------------ */
  /*                           Entries Listener                                */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!hotelId || !businessDate) {
      setEntries([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setLoadError("");

    const entriesQuery = query(
      collection(db, "dayBookEntries"),

      where("hotelId", "==", hotelId),

      where(
        "businessDate",
        "==",
        businessDate
      )
    );

    return onSnapshot(
      entriesQuery,

      (snapshot) => {
        const list = snapshot.docs
          .map((entryDocument) => ({
            id: entryDocument.id,
            ...entryDocument.data(),
          }))
          .sort(
            (firstEntry, secondEntry) =>
              getTimestamp(
                firstEntry.eventAt ||
                  firstEntry.createdAt
              ) -
              getTimestamp(
                secondEntry.eventAt ||
                  secondEntry.createdAt
              )
          );

        setEntries(list);
        setLoading(false);
      },

      (error) => {
        console.error(
          "Day Book entries load error:",
          error
        );

        setEntries([]);
        setLoading(false);

        setLoadError(
          error?.message ||
            "Day Book entries load nahi hui."
        );
      }
    );
  }, [hotelId, businessDate]);

  /* ------------------------------------------------------------------------ */
  /*                           Summary Listener                                */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!hotelId || !businessDate) {
      setSummary({});
      return undefined;
    }

    setSummaryReady(false);
    setOpeningReady(false);
    const summaryReference = doc(
      db,
      "dayBookSummaries",
      createSummaryId(
        hotelId,
        businessDate
      )
    );

    return onSnapshot(
      summaryReference,

      (snapshot) => {
        const data = snapshot.exists()
          ? snapshot.data()
          : {};

        setSummary(data);
        setSummaryReady(true);

        if (
          data.openingCash !== undefined
        ) {
          setOpeningReady(true);
          setOpeningCashInput(
            String(
              toAmount(data.openingCash)
            )
          );

          setOpeningCashSource(
            data.openingCashSource ||
              "saved"
          );
        }
      },

      (error) => {
        console.error(
          "Day Book summary load error:",
          error
        );
      }
    );
  }, [hotelId, businessDate]);

  /* ------------------------------------------------------------------------ */
  /*               Auto Load Previous Day Closing as Opening Cash              */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    let active = true;

    const loadPreviousClosing = async () => {
      if (
        !hotelId ||
        !businessDate ||
        !summaryReady ||
        summary.openingCash !== undefined
      ) {
        return;
      }

      const previousDate =
        getPreviousBusinessDate(
          businessDate
        );

      if (!previousDate) return;

      try {
        const previousSnapshot =
          await getDoc(
            doc(
              db,
              "dayBookSummaries",
              createSummaryId(
                hotelId,
                previousDate
              )
            )
          );

        if (!active) return;

        if (previousSnapshot.exists()) {
          const previousData =
            previousSnapshot.data();

          const previousClosing =
            toAmount(
              previousData.closingCash
            );

          setOpeningCashInput(
            String(previousClosing)
          );

          setOpeningCashSource(
            `Previous closing: ${previousDate}`
          );
        } else {
          setOpeningCashInput("0");
          setOpeningCashSource(
            "No previous closing found"
          );
        }
        if (active) setOpeningReady(true);
      } catch (error) {
        console.error(
          "Previous closing load error:",
          error
        );
      }
    };

    loadPreviousClosing();

    return () => {
      active = false;
    };
  }, [
    hotelId,
    businessDate,
    summary.openingCash, summaryReady,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                             Totals                                        */
  /* ------------------------------------------------------------------------ */

  const totals = useMemo(() => {
    const result = {
      roomRevenue: 0,
      foodRevenue: 0,
      laundryRevenue: 0,
      extraRevenue: 0,

      advanceRevenue: 0,

      cashReceived: 0,
      upiReceived: 0,
      cardReceived: 0,
      bankReceived: 0,
      fabReceived: 0,
      brevistayReceived: 0,
      otaReceived: 0,
      otherReceived: 0,

      cashExpense: 0,
      onlineExpense: 0,

      cashHandedOver: 0,

      salesTotal: 0,
      paymentsReceived: 0,
      onlineReceived: 0,
      expensesTotal: 0,

      openingCash: toAmount(
        openingCashInput
      ),

      closingCash: 0,
    };

    withLegacyReceiptFallbacks(entries).forEach((entry) => {
      const amount =
        getEntryAmount(entry);

      if (isRevenueEntry(entry)) {
        if (
          entry.category === "room"
        ) {
          result.roomRevenue += amount;
        } else if (
          entry.category === "food"
        ) {
          result.foodRevenue += amount;
        } else if (
          entry.category ===
          "laundry"
        ) {
          result.laundryRevenue +=
            amount;
        } else if (
          entry.category === "extra"
        ) {
          result.extraRevenue +=
            amount;
        }

        if (isAdvanceEntry(entry)) {
          result.advanceRevenue +=
            amount;
        }
      }

      if (isPaymentEntry(entry)) {
        const mode =
          getPaymentModeKey(
            entry.paymentMode
          );

        if (mode === "cash") {
          result.cashReceived += amount;
        } else if (mode === "upi") {
          result.upiReceived += amount;
        } else if (mode === "card") {
          result.cardReceived += amount;
        } else if (mode === "bank") {
          result.bankReceived += amount;
        } else if (mode === "fab") {
          result.fabReceived += amount;
        } else if (
          mode === "brevistay"
        ) {
          result.brevistayReceived +=
            amount;
        } else if (mode === "ota") {
          result.otaReceived += amount;
        } else {
          result.otherReceived +=
            amount;
        }

        if (isAdvanceEntry(entry)) {
          result.advanceRevenue +=
            amount;
        }
      }

      if (isExpenseEntry(entry)) {
        if (
          isCashMode(
            entry.paymentMode
          )
        ) {
          result.cashExpense += amount;
        } else {
          result.onlineExpense +=
            amount;
        }
      }

      if (
        isCashHandoverEntry(entry)
      ) {
        result.cashHandedOver += amount;
      }
    });

    result.salesTotal = roundMoney(
      result.roomRevenue +
        result.foodRevenue +
        result.laundryRevenue +
        result.extraRevenue
    );

    result.onlineReceived = roundMoney(
      result.upiReceived +
        result.cardReceived +
        result.bankReceived +
        result.fabReceived +
        result.brevistayReceived +
        result.otaReceived +
        result.otherReceived
    );

    result.paymentsReceived =
      roundMoney(
        result.cashReceived +
          result.onlineReceived
      );

    result.expensesTotal = roundMoney(
      result.cashExpense +
        result.onlineExpense
    );

    result.closingCash = roundMoney(
      result.openingCash +
        result.cashReceived -
        result.cashExpense -
        result.cashHandedOver
    );

    Object.keys(result).forEach(
      (key) => {
        if (
          typeof result[key] ===
          "number"
        ) {
          result[key] = roundMoney(
            result[key]
          );
        }
      }
    );

    return result;
  }, [entries, openingCashInput]);

  /* ------------------------------------------------------------------------ */
  /*                    Save Calculated Summary Automatically                  */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (
      !hotelId ||
      !businessDate ||
      loading || !summaryReady || !openingReady
    ) {
      return;
    }

    const timeout = setTimeout(() => {
      // Server reads current ledger entries; an old device cannot submit stale totals.
      httpsCallable(getFunctions(app, 'asia-south1'), 'refreshDayBookSummary')({hotelId, businessDate})
        .catch(error => console.error('Day Book summary save error:', error.message));
    }, 600);

    return () =>
      clearTimeout(timeout);
  }, [
    hotelId,
    hotelName,
    businessDate,
    totals,
    entries.length,
    loading, summaryReady, openingReady,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                           Save Opening Cash                               */
  /* ------------------------------------------------------------------------ */

  const saveOpeningCash = async () => {
    if (!hotelId || saving) return;

    const amount = Math.max(
      0,
      toAmount(openingCashInput)
    );

    setSaving(true);

    try {
      await setDoc(
        doc(
          db,
          "dayBookSummaries",
          createSummaryId(
            hotelId,
            businessDate
          )
        ),

        {
          hotelId,
          hotelName:
            hotelName || "",

          businessDate,

          openingCash: amount,

          openingCashSource:
            openingCashSource ||
            "Manual",

          openingCashSavedAt:
            serverTimestamp(),

          updatedAt:
            serverTimestamp(),
        },

        {
          merge: true,
        }
      );

      alert(
        "Opening cash save ho gaya ✅"
      );
    } catch (error) {
      console.error(
        "Opening cash save error:",
        error
      );

      alert(
        "Opening cash save nahi hua: " +
          (error?.message ||
            error)
      );
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                              Add Expense                                  */
  /* ------------------------------------------------------------------------ */

  const addExpense = async (event) => {
    event.preventDefault();

    if (saving) return;

    const amount = Math.max(
      0,
      toAmount(expenseForm.amount)
    );

    if (!clean(expenseForm.title)) {
      alert(
        "Expense ka naam required hai."
      );
      return;
    }

    if (amount <= 0) {
      alert(
        "Valid expense amount enter karo."
      );
      return;
    }

    if (
      !isCashMode(
        expenseForm.paymentMode
      ) &&
      !clean(
        expenseForm.transactionReference
      )
    ) {
      alert(
        "Online expense ka transaction reference enter karo."
      );
      return;
    }

    setSaving(true);

    try {
      const now = new Date();

      const expenseRef = doc(collection(db, 'hotelExpenses'));
      const dayBookRef = doc(collection(db, 'dayBookEntries'));
      const payload = {
          hotelId,
          hotelName:
            hotelName || "",

          businessDate,

          entryType: "expense",

          category:
            expenseForm.category,

          title: clean(
            expenseForm.title
          ),

          amount,

          paymentMode:
            expenseForm.paymentMode,

          transactionReference:
            clean(
              expenseForm.transactionReference
            ),

          note: clean(
            expenseForm.note
          ),

          eventAt:
            now.toISOString(),

          createdAt:
            serverTimestamp(),

          source:
            "manager_expense",
        };
      const batch = writeBatch(db);
      batch.set(expenseRef, {...payload, expenseDate: businessDate, expenseAt: payload.eventAt, dayBookEntryId: dayBookRef.id});
      batch.set(dayBookRef, {...payload, expenseId: expenseRef.id});
      await batch.commit();

      setExpenseForm(
        createExpenseForm()
      );

      alert("Expense add ho gaya ✅");
    } catch (error) {
      console.error(
        "Expense add error:",
        error
      );

      alert(
        "Expense add nahi hua: " +
          (error?.message ||
            error)
      );
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                    Add Advance / Separate Receipt                         */
  /* ------------------------------------------------------------------------ */

  const addManualReceipt = async (
    event
  ) => {
    event.preventDefault();

    if (saving) return;

    const amount = Math.max(
      0,
      toAmount(receiptForm.amount)
    );

    if (!clean(receiptForm.title)) {
      alert(
        "Received amount ka reason enter karo."
      );
      return;
    }

    if (amount <= 0) {
      alert(
        "Valid received amount enter karo."
      );
      return;
    }

    if (
      !isCashMode(
        receiptForm.paymentMode
      ) &&
      !clean(
        receiptForm.transactionReference
      )
    ) {
      alert(
        "Online payment ka transaction reference enter karo."
      );
      return;
    }

    setSaving(true);

    try {
      const now = new Date();

      await addDoc(
        collection(
          db,
          "dayBookEntries"
        ),

        {
          hotelId,
          hotelName:
            hotelName || "",

          businessDate,

          entryType: "payment",

          category:
            receiptForm.category ||
            "advance",

          title: clean(
            receiptForm.title
          ),

          amount,

          paymentMode:
            receiptForm.paymentMode,

          room: clean(
            receiptForm.room
          ).toUpperCase(),

          guestName: clean(
            receiptForm.guestName
          ),

          transactionReference:
            clean(
              receiptForm.transactionReference
            ),

          note: clean(
            receiptForm.note
          ),

          eventAt:
            now.toISOString(),

          createdAt:
            serverTimestamp(),

          source:
            receiptForm.category ||
            "manual_receipt",
        }
      );

      setReceiptForm(
        createManualReceiptForm()
      );

      alert(
        "Received amount Day Book me add ho gaya ✅"
      );
    } catch (error) {
      console.error(
        "Manual receipt add error:",
        error
      );

      alert(
        "Received amount add nahi hua: " +
          (error?.message ||
            error)
      );
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                             Cash Handover                                 */
  /* ------------------------------------------------------------------------ */

  const addCashHandover = async (
    event
  ) => {
    event.preventDefault();

    if (saving) return;

    const amount = Math.max(
      0,
      toAmount(
        cashHandoverForm.amount
      )
    );

    if (amount <= 0) {
      alert(
        "Valid cash handover amount enter karo."
      );
      return;
    }

    if (
      !clean(
        cashHandoverForm.receivedBy
      )
    ) {
      alert(
        "Cash receive karne wale Sir/Person ka naam enter karo."
      );
      return;
    }

    if (
      amount >
      totals.closingCash
    ) {
      const confirmed =
        window.confirm(
          `Cash handover ${formatMoney(
            amount
          )} current calculated cash ${formatMoney(
            totals.closingCash
          )} se zyada hai.\n\nContinue karna hai?`
        );

      if (!confirmed) return;
    }

    setSaving(true);

    try {
      const now = new Date();

      await addDoc(
        collection(
          db,
          "dayBookEntries"
        ),

        {
          hotelId,
          hotelName:
            hotelName || "",

          businessDate,

          entryType:
            "cash_handover",

          category:
            "cash_handover",

          title: `Cash received by ${clean(
            cashHandoverForm.receivedBy
          )}`,

          amount,

          paymentMode: "Cash",

          receivedBy: clean(
            cashHandoverForm.receivedBy
          ),

          note: clean(
            cashHandoverForm.note
          ),

          eventAt:
            now.toISOString(),

          createdAt:
            serverTimestamp(),

          source:
            "cash_handover",
        }
      );

      setCashHandoverForm(
        createCashHandoverForm()
      );

      alert(
        "Cash handover entry save ho gayi ✅"
      );
    } catch (error) {
      console.error(
        "Cash handover error:",
        error
      );

      alert(
        "Cash handover save nahi hua: " +
          (error?.message ||
            error)
      );
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                   Manual Advance / Pending Receipts                       */
  /* ------------------------------------------------------------------------ */

  const resetAdvanceManualForm =
    () => {
      setAdvanceManualEditId("");

      setAdvanceManualForm({
        amount: "",
        room: "",
        receiptType:
          "Advance Received",
        paymentMode: "Cash",
        transactionReference: "",
        guestName: "",
        note: "",
        time: "",
      });
    };

  const updateAdvanceManualField = (
    field,
    value
  ) => {
    setAdvanceManualForm(
      (current) => ({
        ...current,
        [field]: value,
      })
    );
  };

  const getActiveStayForRoom =
    async (roomValue) => {
      const room =
        clean(roomValue)
          .toUpperCase();

      if (
        !hotelId ||
        !room
      ) {
        return null;
      }

      const snap =
        await getDocs(
          query(
            collection(
              db,
              "hotelStays"
            ),
            where(
              "hotelId",
              "==",
              hotelId
            )
          )
        );

      const docs =
        snap.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

      return (
        docs.find(
          (item) =>
            clean(item.room)
              .toUpperCase() ===
              room &&
            ![
              "checked_out",
              "cancelled",
            ].includes(
              lower(
                item.status
              )
            )
        ) || null
      );
    };

  const calculateStayGrandTotal = (
    stayData
  ) => {
    const roomRent =
      toAmount(
        stayData?.tariff
      );

    const food =
      toAmount(
        stayData?.foodTotal
      );

    const laundry =
      toAmount(
        stayData?.laundryTotal
      );

    const extra =
      toAmount(
        stayData?.extraTotal
      );

    const discount =
      Math.max(
        0,
        toAmount(
          stayData?.discountAmount
        )
      );

    const gstRate =
      Math.max(
        0,
        toAmount(
          stayData?.gstRate
        )
      );

    const gross =
      roomRent +
      food +
      laundry +
      extra;

    const taxable =
      Math.max(
        0,
        gross - discount
      );

    const gst =
      roundMoney(
        (taxable *
          gstRate) /
          100
      );

    return roundMoney(
      taxable + gst
    );
  };

  const commitManualReceipt = async ({reference, payload, remove = false, candidateStayId = ''}) => {
    return runTransaction(db, async transaction => {
      const previous = await transaction.get(reference);
      const old = previous.exists() ? previous.data() : null;
      if (old && old.hotelId !== hotelId) throw new Error('Hotel mismatch.');
      // Stored identity survives checkouts and subsequent guests in the same room.
      const stayId = old?.stayId || (!old ? candidateStayId : '');
      if (old && !stayId && old.category === 'manual_advance_pending' && !old.standaloneReceipt) {
        throw new Error('Legacy receipt ka original stay link missing hai. Usko pehle reconcile karein; current guest ko edit nahi kiya gaya.');
      }
      const stayRef = stayId ? doc(db, 'hotelStays', stayId) : null;
      const staySnap = stayRef ? await transaction.get(stayRef) : null;
      let invoiceRef = null; let invoiceSnap = null;
      if (staySnap?.exists() && staySnap.data().invoiceNumber) {
        invoiceRef = doc(db, 'hotelInvoices', staySnap.data().invoiceNumber);
        invoiceSnap = await transaction.get(invoiceRef);
      }
      if (stayRef && !staySnap?.exists()) throw new Error('Original stay missing hai.');
      if (staySnap?.exists()) {
        const stay = staySnap.data();
        if (stay.hotelId !== hotelId) throw new Error('Stay hotel mismatch.');
        if (!old && (stay.status !== 'checked_in' || stay.checkedOut || String(stay.room).toUpperCase() !== String(payload.room).toUpperCase())) throw new Error('Guest/room ab change ho chuka hai. Fresh stay kholein.');
        if (old && payload && String(old.room) !== String(payload.room)) throw new Error('Linked receipt ka room change nahi kar sakte. Original payment correct karein.');
        const payments = Array.isArray(stay.payments) ? stay.payments : [];
        const linked = payments.find(payment => payment.dayBookEntryId === reference.id);
        const oldAmount = linked ? toAmount(linked.amount) : 0;
        const newAmount = remove ? 0 : toAmount(payload.amount);
        const nextPaid = roundMoney(calculatePaidTotal(stay) + newAmount - oldAmount);
        if (nextPaid < 0) throw new Error('Payment reversal allocated amount se zyada hai.');
        const updatedPayments = payments.filter(payment => payment.dayBookEntryId !== reference.id);
        if (!remove) updatedPayments.push({type: 'payment', title: payload.title, amount: newAmount,
          paymentMode: payload.paymentMode, transactionReference: payload.transactionReference || '',
          source: 'daybook_manual_advance_pending', dayBookEntryId: reference.id, addedAt: payload.eventAt});
        const fields = financialFields({...stay, paidTotal: nextPaid});
        transaction.update(stayRef, {...fields, paidTotal: nextPaid, payments: updatedPayments, updatedAt: serverTimestamp()});
        if (invoiceSnap?.exists()) transaction.update(invoiceRef, {...fields, paidTotal: nextPaid, payments: updatedPayments, updatedAt: serverTimestamp()});
      }
      if (remove) transaction.delete(reference);
      else transaction.set(reference, {...payload, stayId, standaloneReceipt: !stayId, createdAt: old?.createdAt || serverTimestamp(), updatedAt: serverTimestamp()});
    });
  };

  const saveManualAdvancePending = async () => {
    if (advanceManualSaving) return;
    const amount = roundMoney(toAmount(advanceManualForm.amount));
    const room = clean(advanceManualForm.room).toUpperCase();
    if (amount <= 0 || !room) return alert('Valid room aur amount required hai.');
    if (advanceManualForm.paymentMode !== 'Cash' && !clean(advanceManualForm.transactionReference)) return alert('Online reference required hai.');
    setAdvanceManualSaving(true);
    try {
      const reference = advanceManualEditId ? doc(db, 'dayBookEntries', advanceManualEditId) : doc(collection(db, 'dayBookEntries'));
      const eventDate = getBusinessTime(businessDate, advanceManualForm.time || new Date().toLocaleTimeString('en-GB', {timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit'}));
      if (Number.isNaN(eventDate.getTime())) throw new Error('Invalid receipt time.');
      const activeStay = advanceManualEditId ? null : await getActiveStayForRoom(room);
      const candidateStayId = activeStay && new Date(activeStay.checkInAt).getTime() <= eventDate.getTime() ? activeStay.id : '';
      const payload = {hotelId, hotelName: hotelName || '', businessDate, entryType: 'payment',
        category: 'manual_advance_pending', source: 'manual_advance_pending', amount, room,
        title: clean(advanceManualForm.receiptType) || 'Advance / Pending Received',
        guestName: clean(advanceManualForm.guestName), paymentMode: clean(advanceManualForm.paymentMode) || 'Cash',
        transactionReference: clean(advanceManualForm.transactionReference), note: clean(advanceManualForm.note),
        eventAt: eventDate.toISOString(), hideFromRegister: true,
        ledgerRoom: room, ledgerGuestName: clean(advanceManualForm.guestName), ledgerNote: clean(advanceManualForm.note),
        ledgerTransactionReference: clean(advanceManualForm.transactionReference)};
      await commitManualReceipt({reference, payload, candidateStayId});
      resetAdvanceManualForm();
      setAdvanceManualEditId('');
      setAdvanceManualFormOpen(false);
      alert('Receipt save ho gayi ✅');
    } catch (error) {alert('Receipt save nahi hui: ' + error.message);}
    finally {setAdvanceManualSaving(false);}
  };

  const editManualAdvancePending =
    (entry) => {
      const date =
        getDateObject(
          entry?.eventAt
        );

      setAdvanceManualEditId(
        entry.id
      );

      setAdvanceManualForm({
        amount:
          String(
            toAmount(
              entry.amount
            )
          ),
        room:
          clean(
            entry.room
          ).toUpperCase(),
        receiptType:
          clean(
            entry.title
          ) ||
          "Advance Received",
        paymentMode:
          clean(
            entry.paymentMode
          ) ||
          "Cash",
        transactionReference:
          getEntryTransactionReference(
            entry
          ),
        guestName:
          clean(
            entry.guestName
          ),
        note:
          clean(
            entry.note
          ),
        time: date
          ? `${String(
              date.getHours()
            ).padStart(
              2,
              "0"
            )}:${String(
              date.getMinutes()
            ).padStart(
              2,
              "0"
            )}`
          : "",
      });

      setAdvanceManualFormOpen(
        true
      );
    };

  const deleteManualAdvancePending =
    async (entry) => {
      if (
        advanceManualSaving ||
        !entry?.id
      ) {
        return;
      }

      const ok =
        window.confirm(
          `₹${toAmount(
            entry.amount
          ).toFixed(
            2
          )} ki Advance/Pending entry delete karni hai?`
        );

      if (!ok) return;

      setAdvanceManualSaving(
        true
      );

      try {
        await commitManualReceipt({reference: doc(db, 'dayBookEntries', entry.id), remove: true});
      } catch (error) {
        console.error(
          "Manual advance delete error:",
          error
        );

        alert(
          "Entry delete nahi hui: " +
            (error?.message ||
              error)
        );
      } finally {
        setAdvanceManualSaving(
          false
        );
      }
    };

  /* ------------------------------------------------------------------------ */
  /*                    Editable Hotel Register Rows                           */
  /* ------------------------------------------------------------------------ */

  const updateEditRegisterField = (
    field,
    value
  ) => {
    setEditRegisterRow(
      (current) => ({
        ...current,
        [field]: value,
      })
    );
  };

  const updateManualRegisterField = (
    field,
    value
  ) => {
    setManualRegisterRow(
      (current) => ({
        ...current,
        [field]: value,
      })
    );
  };

  const startRegisterEdit = (
    entry,
    index
  ) => {
    if (saving) return;

    const row =
      getRegisterRow(
        entry,
        index
      );

    setEditingEntryId(
      entry.id
    );

    setEditRegisterRow({
      ...createEmptyRegisterRow(),

      ...Object.fromEntries(
        Object.entries(row).map(
          ([key, value]) => [
            key,
            value === 0
              ? ""
              : String(
                  value ?? ""
                ),
          ]
        )
      ),
    });
  };

  const cancelRegisterEdit = () => {
    setEditingEntryId("");

    setEditRegisterRow(
      createEmptyRegisterRow()
    );
  };

  const saveRegisterEdit = async (
    entryId
  ) => {
    if (
      !entryId ||
      saving
    ) {
      return;
    }

    setSaving(true);

    try {
      await updateDoc(
        doc(
          db,
          "dayBookEntries",
          entryId
        ),

        {
          ...registerRowToFirestore(
            editRegisterRow
          ),

          updatedAt:
            serverTimestamp(),
        }
      );

      setEditingEntryId("");

      setEditRegisterRow(
        createEmptyRegisterRow()
      );
    } catch (error) {
      console.error(
        "Register row edit error:",
        error
      );

      alert(
        "Register row save nahi hui: " +
          (error?.message ||
            error)
      );
    } finally {
      setSaving(false);
    }
  };

  const addManualRegisterRow =
    async () => {
      if (
        saving ||
        !hotelId
      ) {
        return;
      }

      const row =
        manualRegisterRow;

      const hasAnyValue =
        Object.values(row).some(
          (value) =>
            clean(value) !== ""
        );

      if (!hasAnyValue) {
        alert(
          "Manual row me kam se kam ek value enter karo."
        );
        return;
      }

      setSaving(true);

      try {
        const now =
          new Date();

        await addDoc(
          collection(
            db,
            "dayBookEntries"
          ),

          {
            hotelId,

            hotelName:
              hotelName || "",

            businessDate,

            entryType:
              "manual_register",

            category:
              "manual_register",

            title:
              clean(
                row.guestName
              ) ||
              clean(row.note) ||
              "Manual Register Entry",

            room:
              clean(
                row.room
              ).toUpperCase(),

            guestName:
              clean(
                row.guestName
              ),

            amount:
              Math.max(
                0,
                toAmount(
                  row.tariff
                )
              ),

            paymentMode: "",

            transactionReference:
              clean(
                row.transactionReference
              ),

            note:
              clean(row.note),

            eventAt:
              now.toISOString(),

            createdAt:
              serverTimestamp(),

            source:
              "manual_register",

            ...registerRowToFirestore(
              row
            ),
          }
        );

        setManualRegisterRow(
          createEmptyRegisterRow()
        );

        setManualRowOpen(
          false
        );
      } catch (error) {
        console.error(
          "Manual register row add error:",
          error
        );

        alert(
          "Manual row add nahi hui: " +
            (error?.message ||
              error)
        );
      } finally {
        setSaving(false);
      }
    };

  const registerTotals =
    useMemo(() => {
      const result = {
        tariff: 0,
        cash: 0,
        online: 0,
        fab: 0,
        brevistay: 0,
        balance: 0,
        advance: 0,
        expense: 0,
      };


      entries
        .filter(
          (entry) =>
            entry?.hideFromRegister !==
              true &&
            isMainRoomRegisterEntry(
              entry
            )
        )
        .forEach(
        (entry, index) => {
          const row =
            getRegisterRow(
              entry,
              index
            );

          result.tariff +=
            toAmount(
              row.tariff
            );

          result.cash +=
            toAmount(
              row.cash
            );

          result.online +=
            toAmount(
              row.online
            );

          result.fab +=
            toAmount(
              row.fab
            );

          result.brevistay +=
            toAmount(
              row.brevistay
            );

          result.balance +=
            toAmount(
              row.balance
            );

          result.advance +=
            toAmount(
              row.advance
            );

          result.expense +=
            toAmount(
              row.expense
            );
        }
      );

      Object.keys(
        result
      ).forEach((key) => {
        result[key] =
          roundMoney(
            result[key]
          );
      });

      return result;
    }, [entries]);


  const foodReceiptLedger =
    useMemo(() => {
      const rows = entries
        .filter(
          isFoodReceiptEntry
        )
        .map(
          buildSideLedgerLine
        );

      return rows.sort(
        (a, b) =>
          String(a.time).localeCompare(
            String(b.time)
          )
      );
    }, [entries]);

  const expenseReceiptLedger =
    useMemo(() => {
      const rows = entries
        .filter(
          (entry) =>
            isExpenseEntry(
              entry
            ) ||
            isCashHandoverEntry(
              entry
            )
        )
        .map(
          buildSideLedgerLine
        );

      return rows.sort(
        (a, b) =>
          String(a.time).localeCompare(
            String(b.time)
          )
      );
    }, [entries]);

  const expenseReceiptSummary =
    useMemo(() => {
      const total =
        expenseReceiptLedger.reduce(
          (sum, row) =>
            sum +
            toAmount(
              row.amount
            ),
          0
        );

      return {
        cash: 0,
        online: 0,
        total:
          roundMoney(total),
      };
    }, [expenseReceiptLedger]);

  const advancePendingLedger =
    useMemo(() => {
      const rows = entries
        .filter(
          (entry) =>
            entry?.category ===
              "manual_advance_pending" ||
            entry?.source ===
              "manual_advance_pending" ||
            entry?.category ===
              "checkin_excess_received" ||
            entry?.category === "cnu_excess_received" ||
            entry?.category ===
              "pending_received" ||
            entry?.source ===
              "pending_received" ||
            entry?.category ===
              "booking_diary_advance"
        )
        .map(
          buildSideLedgerLine
        );

      return rows.sort(
        (a, b) =>
          String(a.time).localeCompare(
            String(b.time)
          )
      );
    }, [entries]);

  const foodReceiptSummary =
    useMemo(() => {
      const result = {
        cash: 0,
        online: 0,
        total: 0,
      };

      foodReceiptLedger.forEach(
        (row) => {
          const amount =
            toAmount(
              row.amount
            );

          result.total += amount;

          if (
            getPaymentModeKey(
              row.mode
            ) === "cash"
          ) {
            result.cash +=
              amount;
          } else {
            result.online +=
              amount;
          }
        }
      );

      return {
        cash:
          roundMoney(
            result.cash
          ),
        online:
          roundMoney(
            result.online
          ),
        total:
          roundMoney(
            result.total
          ),
      };
    }, [foodReceiptLedger]);

  const advancePendingSummary =
    useMemo(() => {
      const result = {
        cash: 0,
        online: 0,
        total: 0,
      };

      advancePendingLedger.forEach(
        (row) => {
          const amount =
            toAmount(
              row.amount
            );

          result.total += amount;

          if (
            getPaymentModeKey(
              row.mode
            ) === "cash"
          ) {
            result.cash +=
              amount;
          } else {
            result.online +=
              amount;
          }
        }
      );

      return {
        cash:
          roundMoney(
            result.cash
          ),
        online:
          roundMoney(
            result.online
          ),
        total:
          roundMoney(
            result.total
          ),
      };
    }, [advancePendingLedger]);

  /* ------------------------------------------------------------------------ */
  /*                              Filter Entries                               */
  /* ------------------------------------------------------------------------ */

  const filteredEntries = useMemo(() => {
    const searchTerm = lower(search);

    return entries.filter((entry) => {
      /*
       * Main Hotel Day Book Register = sirf ROOM entries.
       * Food, Advance/Pending, Expense, receipts sab apne
       * alag reference boxes me dikhenge.
       */
      if (
        entry?.hideFromRegister ===
          true ||
        !isMainRoomRegisterEntry(
          entry
        )
      ) {
        return false;
      }

      const entryText = [
        entry.title,
        entry.room,
        entry.guestName,
        entry.paymentMode,
        entry.transactionReference,
        entry.note,
        entry.category,
        entry.serialNumber,
        entry.adRegisterSerialNo,
        entry.ledgerSerial,
        entry.ledgerRoom,
        entry.ledgerGuestName,
        entry.ledgerNote,
        entry.ledgerTransactionReference,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        !searchTerm ||
        entryText.includes(searchTerm);

      const matchesEntryType =
        entryTypeFilter === "all" ||
        (entryTypeFilter ===
          "advance" &&
          isAdvanceEntry(entry)) ||
        (entryTypeFilter ===
          "cash_handover" &&
          isCashHandoverEntry(entry)) ||
        entry.entryType ===
          entryTypeFilter;

      const mode =
        getPaymentModeKey(
          entry.paymentMode
        );

      const matchesPayment =
        paymentFilter === "all" ||
        (paymentFilter ===
          "online" &&
          isOnlineEntry(entry)) ||
        mode === paymentFilter;

      return (
        matchesSearch &&
        matchesEntryType &&
        matchesPayment
      );
    });
  }, [
    entries,
    search,
    entryTypeFilter,
    paymentFilter,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                         Copy Closing to Next Day                          */
  /* ------------------------------------------------------------------------ */

  const carryClosingToNextDay =
    async () => {
      if (saving) return;

      const nextDate =
        getNextBusinessDate(
          businessDate
        );

      if (!nextDate) {
        alert(
          "Next business date calculate nahi hui."
        );
        return;
      }

      const confirmed =
        window.confirm(
          `${formatMoney(
            totals.closingCash
          )} ko ${nextDate} ka opening cash save karna hai?`
        );

      if (!confirmed) return;

      setSaving(true);

      try {
        const response = await httpsCallable(getFunctions(app, 'asia-south1'), 'carryDayBookClosing')({hotelId, businessDate});
        alert(
          `${nextDate} ka opening cash ₹${response.data.openingCash} save ho gaya ✅`
        );
      } catch (error) {
        console.error(
          "Next opening save error:",
          error
        );

        alert(
          "Next opening save nahi hua: " +
            (error?.message ||
              error)
        );
      } finally {
        setSaving(false);
      }
    };

  /* ------------------------------------------------------------------------ */
  /*                                Print                                      */
  /* ------------------------------------------------------------------------ */

  const printDayBook = () => {
    const printWindow = window.open(
      "",
      "_blank",
      "width=1450,height=950"
    );

    if (!printWindow) {
      alert(
        "Print popup block ho gaya. Browser me popups allow karo."
      );
      return;
    }

    const tableRows = filteredEntries
      .map((entry, index) => {
        const row =
          getRegisterRow(
            entry,
            index
          );

        const cashText =
          row.cashLabel ||
          (row.cash > 0
            ? formatMoney(row.cash)
            : "-");

        const onlineText =
          row.onlineLabel ||
          (row.online > 0
            ? formatMoney(row.online)
            : "-");

        const onlineWithCode = `${onlineText}${
          row.transactionReference
            ? `<br/><small>(${escapePrintValue(clean(
                row.transactionReference
              ))})</small>`
            : ""
        }`;

        const fabText =
          row.fabLabel ||
          (row.fab > 0
            ? formatMoney(row.fab)
            : "-");

        const brevistayText =
          row.brevistayLabel ||
          (row.brevistay > 0
            ? formatMoney(row.brevistay)
            : "-");

        return `
          <tr>
            <td>${escapePrintValue(clean(
              row.serial ||
                index + 1
            ))}</td>

            <td>${escapePrintValue(clean(
              row.room || "-"
            ))}</td>

            <td>${row.tariff
              ? formatMoney(
                  row.tariff
                )
              : "-"
            }</td>

            <td>${cashText}</td>

            <td class="${
              row.online
                ? "online"
                : ""
            }">
              ${onlineWithCode}
            </td>

            <td>${fabText}</td>

            <td>${brevistayText}</td>

            <td>${escapePrintValue(clean(
              row.checkIn || "-"
            ))}</td>

            <td>${escapePrintValue(clean(
              row.checkOut || "-"
            ))}</td>

            <td>${row.balance
              ? formatMoney(
                  row.balance
                )
              : "-"
            }</td>

            <td>${escapePrintValue(clean(
              row.guestName ||
                "-"
            ))}</td>

            <td>${escapePrintValue(clean(
              row.note || "-"
            ))}</td>
          </tr>
        `;
      })
      .join("");

    const buildPrintLedgerRows = (
      rows,
      type
    ) => {
      if (
        !Array.isArray(rows) ||
        rows.length === 0
      ) {
        return `
          <div class="ledger-empty">
            No entries
          </div>
        `;
      }

      return rows
        .map((row) => {
          const amount =
            formatMoney(
              row?.amount || 0
            );

          const room =
            clean(
              row?.room || "-"
            );

          const time =
            clean(
              row?.time || "-"
            );

          const mode =
            clean(
              row?.mode || "Cash"
            );

          const code =
            clean(
              row?.code || ""
            );

          const guest =
            clean(
              row?.guestName || ""
            );

          const note =
            clean(
              row?.note || ""
            );

          let mainLine = "";

          if (type === "food") {
            mainLine =
              `${amount} → ${room}`;

            if (code) {
              mainLine +=
                ` [${code}]`;
            }
          } else if (
            type === "advance"
          ) {
            mainLine =
              `${amount} → ${
                note ||
                `Adv / Pending Received Room ${room}`
              }`;

            if (
              !mainLine
                .toLowerCase()
                .includes(
                  room.toLowerCase()
                )
            ) {
              mainLine +=
                ` • Room ${room}`;
            }

            if (code) {
              mainLine +=
                ` [${code}]`;
            }
          } else {
            mainLine =
              `${amount} → ${
                note ||
                "Expense"
              }`;

            if (code) {
              mainLine +=
                ` [${code}]`;
            }
          }

          return `
            <div class="ledger-row">
              <div class="ledger-main">
                ${mainLine}
              </div>

              <div class="ledger-meta">
                ${time}
                •
                ${mode}
                ${
                  guest
                    ? `• Guest: ${guest}`
                    : ""
                }
              </div>
            </div>
          `;
        })
        .join("");
    };

    const foodPrintRows =
      buildPrintLedgerRows(
        foodReceiptLedger,
        "food"
      );

    const advancePrintRows =
      buildPrintLedgerRows(
        advancePendingLedger,
        "advance"
      );

    const expensePrintRows =
      buildPrintLedgerRows(
        expenseReceiptLedger,
        "expense"
      );

    printWindow.document.open();

    printWindow.document.write(`
      <!DOCTYPE html>

      <html>
        <head>
          <meta charset="UTF-8" />

          <title>
            Day Book ${businessDate}
          </title>

          <style>
            * {
              box-sizing: border-box;
            }

            body {
              margin: 0;
              padding: 8mm;
              color: #111827;
              font-family: Arial, sans-serif;
            }

            h1 {
              margin: 0;
              text-align: center;
              font-size: 20px;
            }

            .sub {
              margin-top: 4px;
              text-align: center;
              font-size: 10px;
            }

            .summary {
              display: grid;
              grid-template-columns:
                repeat(6,1fr);
              gap: 5px;
              margin: 10px 0;
            }

            .summary div {
              padding: 6px;
              border: 1px solid #64748b;
              text-align: center;
              font-size: 8px;
            }

            .summary strong {
              display: block;
              margin-top: 3px;
              font-size: 10px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            th,
            td {
              padding: ${
                printCompact
                  ? "3px"
                  : "5px"
              };
              border: 1px solid #64748b;
              font-size: ${
                printCompact
                  ? "6px"
                  : "7px"
              };
              vertical-align: top;
            }

            th {
              background: #e2e8f0;
              font-weight: 900;
            }

            .online {
              background: #fef08a;
              font-weight: 900;
            }

            small {
              font-size: 6px;
            }

            .reference-grid {
              display: grid;
              grid-template-columns:
                repeat(3,1fr);
              gap: 7px;
              margin-top: 10px;
              align-items: start;
            }

            .ledger-card {
              border:
                1px solid #64748b;
              min-height: 145px;
              break-inside: avoid;
            }

            .ledger-title {
              padding: 6px 7px;
              border-bottom:
                1px solid #64748b;
              background: #e2e8f0;
              font-size: 9px;
              font-weight: 900;
            }

            .ledger-subtitle {
              margin-top: 2px;
              font-size: 6px;
              font-weight: 400;
              color: #475569;
            }

            .ledger-body {
              min-height: 95px;
            }

            .ledger-row {
              padding: 6px 7px;
              border-bottom:
                1px solid #cbd5e1;
            }

            .ledger-row:last-child {
              border-bottom: 0;
            }

            .ledger-main {
              font-size: 8px;
              font-weight: 900;
              line-height: 1.35;
            }

            .ledger-meta {
              margin-top: 2px;
              color: #475569;
              font-size: 6px;
              line-height: 1.35;
            }

            .ledger-empty {
              padding: 18px 7px;
              text-align: center;
              color: #94a3b8;
              font-size: 7px;
            }

            .ledger-totals {
              display: grid;
              grid-template-columns:
                repeat(3,1fr);
              border-top:
                1px solid #64748b;
              background: #f8fafc;
            }

            .ledger-totals div {
              padding: 5px 4px;
              border-right:
                1px solid #94a3b8;
              text-align: center;
              font-size: 6px;
            }

            .ledger-totals div:last-child {
              border-right: 0;
            }

            .ledger-totals strong {
              display: block;
              margin-top: 2px;
              font-size: 8px;
            }

            @page {
              size: A4 landscape;
              margin: 6mm;
            }
          </style>
        </head>

        <body>
          <h1>
            ${escapePrintValue(clean(
              hotelName || hotelId
            ))} — DIGITAL DAY BOOK
          </h1>

          <div class="sub">
            Business Date:
            ${businessDate}
            |
            7:00 AM to next day 6:59 AM
          </div>

          <div class="summary">
            <div>
              Opening Cash
              <strong>
                ${formatMoney(
                  totals.openingCash
                )}
              </strong>
            </div>

            <div>
              Cash Received
              <strong>
                ${formatMoney(
                  totals.cashReceived
                )}
              </strong>
            </div>

            <div>
              Online Received
              <strong>
                ${formatMoney(
                  totals.onlineReceived
                )}
              </strong>
            </div>

            <div>
              Expenses
              <strong>
                ${formatMoney(
                  totals.expensesTotal
                )}
              </strong>
            </div>

            <div>
              Cash Handover
              <strong>
                ${formatMoney(
                  totals.cashHandedOver
                )}
              </strong>
            </div>

            <div>
              Closing Cash
              <strong>
                ${formatMoney(
                  totals.closingCash
                )}
              </strong>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>Room No.</th>
                <th>Tariff</th>
                <th>Cash</th>
                <th>Online / Code</th>
                <th>FAB</th>
                <th>Brevistay</th>
                <th>IN</th>
                <th>OUT</th>
                <th>Balance</th>
                <th>Name</th>
                <th>Remarks</th>
              </tr>
            </thead>

            <tbody>
              ${tableRows}
            </tbody>
          </table>

          <div class="reference-grid">
            <section class="ledger-card">
              <div class="ledger-title">
                FOOD BILL RECEIVED
                <div class="ledger-subtitle">
                  Actual received food payments
                </div>
              </div>

              <div class="ledger-body">
                ${foodPrintRows}
              </div>

              <div class="ledger-totals">
                <div>
                  Cash
                  <strong>
                    ${formatMoney(
                      foodReceiptSummary.cash
                    )}
                  </strong>
                </div>

                <div>
                  Online
                  <strong>
                    ${formatMoney(
                      foodReceiptSummary.online
                    )}
                  </strong>
                </div>

                <div>
                  Total
                  <strong>
                    ${formatMoney(
                      foodReceiptSummary.total
                    )}
                  </strong>
                </div>
              </div>
            </section>

            <section class="ledger-card">
              <div class="ledger-title">
                ADVANCE & PENDING RECEIVED
                <div class="ledger-subtitle">
                  Advance / pending / extra received entries
                </div>
              </div>

              <div class="ledger-body">
                ${advancePrintRows}
              </div>

              <div class="ledger-totals">
                <div>
                  Cash
                  <strong>
                    ${formatMoney(
                      advancePendingSummary.cash
                    )}
                  </strong>
                </div>

                <div>
                  Online
                  <strong>
                    ${formatMoney(
                      advancePendingSummary.online
                    )}
                  </strong>
                </div>

                <div>
                  Total
                  <strong>
                    ${formatMoney(
                      advancePendingSummary.total
                    )}
                  </strong>
                </div>
              </div>
            </section>

            <section class="ledger-card">
              <div class="ledger-title">
                EXPENSE
                <div class="ledger-subtitle">
                  Daily expense entries
                </div>
              </div>

              <div class="ledger-body">
                ${expensePrintRows}
              </div>

              <div class="ledger-totals">
                <div>
                  Entries
                  <strong>
                    ${expenseReceiptLedger.length}
                  </strong>
                </div>

                <div>
                  -
                  <strong>
                    -
                  </strong>
                </div>

                <div>
                  Total
                  <strong>
                    ${formatMoney(
                      expenseReceiptSummary.total
                    )}
                  </strong>
                </div>
              </div>
            </section>
          </div>

          <script>
            window.onload = function () {
              setTimeout(function () {
                window.focus();
                window.print();
              }, 300);
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <section style={sectionStyle}>
      <div style={headerStyle}>
        <div>
          <h3 style={titleStyle}>
            📒 Digital Day Book
          </h3>

          <div style={subtitleStyle}>
            Business day: 7:00 AM se
            agle din 6:59 AM
          </div>
        </div>

        <div style={headerActionsStyle}>
          <input
            type="date"
            value={businessDate}
            onChange={(event) =>
              setBusinessDate(
                event.target.value
              )
            }
            style={inputStyle}
          />

          <label style={compactLabelStyle}>
            <input
              type="checkbox"
              checked={printCompact}
              onChange={(event) =>
                setPrintCompact(
                  event.target.checked
                )
              }
            />

            Compact Print
          </label>

          <button
            type="button"
            onClick={printDayBook}
            style={printButtonStyle}
          >
            🖨 Print Day Book
          </button>
        </div>
      </div>

      {loadError && (
        <div style={errorBoxStyle}>
          {loadError}
        </div>
      )}

      <div style={summaryGridStyle}>
        <SummaryCard
          label="Opening Cash"
          value={totals.openingCash}
        />

        <SummaryCard
          label="Total Sales"
          value={totals.salesTotal}
        />

        <SummaryCard
          label="Cash Received"
          value={totals.cashReceived}
        />

        <SummaryCard
          label="Online Received"
          value={totals.onlineReceived}
        />

        <SummaryCard
          label="Total Expenses"
          value={totals.expensesTotal}
          danger={
            totals.expensesTotal > 0
          }
        />

        <SummaryCard
          label="Cash Handed Over"
          value={
            totals.cashHandedOver
          }
          warning={
            totals.cashHandedOver > 0
          }
        />

        <SummaryCard
          label="Closing Cash"
          value={totals.closingCash}
          strong
          danger={
            totals.closingCash < 0
          }
        />
      </div>

      <div style={cashSectionGridStyle}>
        <section style={panelStyle}>
          <h4 style={panelTitleStyle}>
            Cash Book
          </h4>

          <AmountRow
            label="Opening Cash"
            value={totals.openingCash}
          />

          <AmountRow
            label="Cash Received"
            value={totals.cashReceived}
          />

          <AmountRow
            label="Cash Expenses"
            value={
              -totals.cashExpense
            }
            danger
          />

          <AmountRow
            label="Cash Given to Sir"
            value={
              -totals.cashHandedOver
            }
            danger
          />

          <div style={dividerStyle} />

          <AmountRow
            label="Closing Cash"
            value={totals.closingCash}
            strong
            danger={
              totals.closingCash < 0
            }
          />

          <div style={openingFormStyle}>
            <input
              type="number"
              min="0"
              value={openingCashInput}
              onChange={(event) => {
                setOpeningCashInput(
                  event.target.value
                );

                setOpeningCashSource(
                  "Manual"
                );
              }}
              placeholder="Opening cash"
              style={{
                ...inputStyle,
                flex: 1,
              }}
            />

            <button
              type="button"
              disabled={saving}
              onClick={saveOpeningCash}
              style={primaryButtonStyle}
            >
              Save Opening
            </button>
          </div>

          {openingCashSource && (
            <div style={sourceTextStyle}>
              Source: {openingCashSource}
            </div>
          )}

          <button
            type="button"
            disabled={saving}
            onClick={
              carryClosingToNextDay
            }
            style={carryButtonStyle}
          >
            Carry Closing to Next Day
          </button>
        </section>

        <section style={panelStyle}>
          <h4 style={panelTitleStyle}>
            Revenue & Payment Modes
          </h4>

          <AmountRow
            label="Room Tariff"
            value={
              totals.roomRevenue
            }
          />

          <AmountRow
            label="Food Bill"
            value={
              totals.foodRevenue
            }
          />

          <AmountRow
            label="Laundry"
            value={
              totals.laundryRevenue
            }
          />

          <AmountRow
            label="Extra Services"
            value={
              totals.extraRevenue
            }
          />

          <AmountRow
            label="Advance / Separate Receipts"
            value={
              totals.advanceRevenue
            }
          />

          <div style={dividerStyle} />

          <AmountRow
            label="UPI"
            value={totals.upiReceived}
          />

          <AmountRow
            label="Card"
            value={
              totals.cardReceived
            }
          />

          <AmountRow
            label="Bank Transfer"
            value={
              totals.bankReceived
            }
          />

          <AmountRow
            label="Fab"
            value={
              totals.fabReceived
            }
          />

          <AmountRow
            label="Brevistay"
            value={
              totals.brevistayReceived
            }
          />

          <AmountRow
            label="Other OTA"
            value={
              totals.otaReceived +
              totals.otherReceived
            }
          />
        </section>
      </div>

      <section style={panelStyle}>
        <div style={formTabsStyle}>
          <button
            type="button"
            onClick={() =>
              setActiveForm("expense")
            }
            style={formTabStyle(
              activeForm === "expense"
            )}
          >
            Add Expense
          </button>

          <button
            type="button"
            onClick={() =>
              setActiveForm("receipt")
            }
            style={formTabStyle(
              activeForm === "receipt"
            )}
          >
            Add Advance / Receipt
          </button>

          <button
            type="button"
            onClick={() =>
              setActiveForm(
                "cash_handover"
              )
            }
            style={formTabStyle(
              activeForm ===
                "cash_handover"
            )}
          >
            Cash Given to Sir
          </button>
        </div>

        {activeForm === "expense" && (
          <form
            onSubmit={addExpense}
            style={formStyle}
          >
            <h4 style={panelTitleStyle}>
              Add Daily Expense
            </h4>

            <div style={formGridStyle}>
              <Field label="Expense Name">
                <input
                  value={
                    expenseForm.title
                  }
                  onChange={(event) =>
                    setExpenseForm(
                      (current) => ({
                        ...current,
                        title:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="Kitchen purchase, repair, salary..."
                  style={inputStyle}
                />
              </Field>

              <Field label="Amount">
                <input
                  type="number"
                  min="0"
                  value={
                    expenseForm.amount
                  }
                  onChange={(event) =>
                    setExpenseForm(
                      (current) => ({
                        ...current,
                        amount:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="₹"
                  style={inputStyle}
                />
              </Field>

              <Field label="Payment Mode">
                <select
                  value={
                    expenseForm.paymentMode
                  }
                  onChange={(event) =>
                    setExpenseForm(
                      (current) => ({
                        ...current,
                        paymentMode:
                          event.target.value,
                        transactionReference:
                          event.target.value ===
                          "Cash"
                            ? ""
                            : current.transactionReference,
                      })
                    )
                  }
                  style={inputStyle}
                >
                  <option>Cash</option>
                  <option>UPI</option>
                  <option>Card</option>
                  <option>
                    Bank Transfer
                  </option>
                </select>
              </Field>

              <Field label="Category">
                <select
                  value={
                    expenseForm.category
                  }
                  onChange={(event) =>
                    setExpenseForm(
                      (current) => ({
                        ...current,
                        category:
                          event.target.value,
                      })
                    )
                  }
                  style={inputStyle}
                >
                  <option>Kitchen</option>
                  <option>Salary</option>
                  <option>Fuel</option>
                  <option>Marketing</option>
                  <option>Utility</option>
                  <option>Misc</option>
                </select>
              </Field>

              {expenseForm.paymentMode !==
                "Cash" && (
                <Field label="Transaction Reference">
                  <input
                    value={
                      expenseForm.transactionReference
                    }
                    onChange={(event) =>
                      setExpenseForm(
                        (current) => ({
                          ...current,
                          transactionReference:
                            event.target.value,
                        })
                      )
                    }
                    placeholder="UPI/Card/Bank reference"
                    style={inputStyle}
                  />
                </Field>
              )}

              <Field label="Note">
                <input
                  value={
                    expenseForm.note
                  }
                  onChange={(event) =>
                    setExpenseForm(
                      (current) => ({
                        ...current,
                        note:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="Optional details"
                  style={inputStyle}
                />
              </Field>
            </div>

            <button
              disabled={saving}
              style={fullPrimaryButtonStyle}
            >
              {saving
                ? "Saving..."
                : "Add Expense"}
            </button>
          </form>
        )}

        {activeForm === "receipt" && (
          <form
            onSubmit={
              addManualReceipt
            }
            style={formStyle}
          >
            <h4 style={panelTitleStyle}>
              Add Advance / Separate Amount Received
            </h4>

            <div style={formGridStyle}>
              <Field label="Receipt Type">
                <select
                  value={
                    receiptForm.category
                  }
                  onChange={(event) =>
                    setReceiptForm(
                      (current) => ({
                        ...current,
                        category:
                          event.target.value,
                      })
                    )
                  }
                  style={inputStyle}
                >
                  <option value="advance">
                    Future Booking Advance
                  </option>

                  <option value="pending_received">
                    Pending Amount Received
                  </option>

                  <option value="early_check_in">
                    Early Check-In Charge
                  </option>

                  <option value="late_checkout">
                    Late Checkout Charge
                  </option>

                  <option value="extra_mattress">
                    Extra Mattress
                  </option>

                  <option value="other_receipt">
                    Other Receipt
                  </option>
                </select>
              </Field>

              <Field label="Description">
                <input
                  value={
                    receiptForm.title
                  }
                  onChange={(event) =>
                    setReceiptForm(
                      (current) => ({
                        ...current,
                        title:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="₹4000 pending amount received from Room B6"
                  style={inputStyle}
                />
              </Field>

              <Field label="Amount">
                <input
                  type="number"
                  min="0"
                  value={
                    receiptForm.amount
                  }
                  onChange={(event) =>
                    setReceiptForm(
                      (current) => ({
                        ...current,
                        amount:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="₹"
                  style={inputStyle}
                />
              </Field>

              <Field label="Room Number">
                <input
                  value={
                    receiptForm.room
                  }
                  onChange={(event) =>
                    setReceiptForm(
                      (current) => ({
                        ...current,
                        room:
                          event.target.value.toUpperCase(),
                      })
                    )
                  }
                  placeholder="B6"
                  style={inputStyle}
                />
              </Field>

              <Field label="Guest Name">
                <input
                  value={
                    receiptForm.guestName
                  }
                  onChange={(event) =>
                    setReceiptForm(
                      (current) => ({
                        ...current,
                        guestName:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="Guest name"
                  style={inputStyle}
                />
              </Field>

              <Field label="Payment Mode">
                <select
                  value={
                    receiptForm.paymentMode
                  }
                  onChange={(event) =>
                    setReceiptForm(
                      (current) => ({
                        ...current,
                        paymentMode:
                          event.target.value,
                        transactionReference:
                          event.target.value ===
                          "Cash"
                            ? ""
                            : current.transactionReference,
                      })
                    )
                  }
                  style={inputStyle}
                >
                  <option>Cash</option>
                  <option>UPI</option>
                  <option>Card</option>
                  <option>
                    Bank Transfer
                  </option>
                  <option>Fab</option>
                  <option>Brevistay</option>
                  <option>Other OTA</option>
                </select>
              </Field>

              {receiptForm.paymentMode !==
                "Cash" && (
                <Field label="Transaction Code">
                  <input
                    value={
                      receiptForm.transactionReference
                    }
                    onChange={(event) =>
                      setReceiptForm(
                        (current) => ({
                          ...current,
                          transactionReference:
                            event.target.value,
                        })
                      )
                    }
                    placeholder="Online transaction code"
                    style={inputStyle}
                  />
                </Field>
              )}

              <Field label="Note">
                <input
                  value={
                    receiptForm.note
                  }
                  onChange={(event) =>
                    setReceiptForm(
                      (current) => ({
                        ...current,
                        note:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="Future date / pending details"
                  style={inputStyle}
                />
              </Field>
            </div>

            <button
              disabled={saving}
              style={fullPrimaryButtonStyle}
            >
              {saving
                ? "Saving..."
                : "Add Received Amount"}
            </button>
          </form>
        )}

        {activeForm ===
          "cash_handover" && (
          <form
            onSubmit={
              addCashHandover
            }
            style={formStyle}
          >
            <h4 style={panelTitleStyle}>
              Cash Given / Received by Sir
            </h4>

            <div style={formGridStyle}>
              <Field label="Cash Amount">
                <input
                  type="number"
                  min="0"
                  value={
                    cashHandoverForm.amount
                  }
                  onChange={(event) =>
                    setCashHandoverForm(
                      (current) => ({
                        ...current,
                        amount:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="₹"
                  style={inputStyle}
                />
              </Field>

              <Field label="Received By">
                <input
                  value={
                    cashHandoverForm.receivedBy
                  }
                  onChange={(event) =>
                    setCashHandoverForm(
                      (current) => ({
                        ...current,
                        receivedBy:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="Sir / Owner name"
                  style={inputStyle}
                />
              </Field>

              <Field label="Note">
                <input
                  value={
                    cashHandoverForm.note
                  }
                  onChange={(event) =>
                    setCashHandoverForm(
                      (current) => ({
                        ...current,
                        note:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="Reason / acknowledgement"
                  style={inputStyle}
                />
              </Field>
            </div>

            <div style={handoverWarningStyle}>
              Ye amount total available cash se
              minus hokar Closing Cash me reflect
              hoga.
            </div>

            <button
              disabled={saving}
              style={handoverButtonStyle}
            >
              {saving
                ? "Saving..."
                : "Save Cash Handover"}
            </button>
          </form>
        )}
      </section>

      <section style={registerPanelStyle}>
        <div style={entriesHeaderStyle}>
          <div>
            <h4 style={panelTitleStyle}>
              📖 Hotel Day Book Register
            </h4>

            <div style={subtitleStyle}>
              Physical register style • Har column manually edit ho sakta hai
            </div>
          </div>

          <div style={registerHeaderButtonsStyle}>
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                setManualRowOpen(
                  (current) =>
                    !current
                );

                setManualRegisterRow(
                  createEmptyRegisterRow()
                );
              }}
              style={addRowButtonStyle}
            >
              {manualRowOpen
                ? "✕ Close Manual Row"
                : "＋ Add Manual Row"}
            </button>
          </div>
        </div>

        <div style={filtersStyle}>
          <input
            value={search}
            onChange={(event) =>
              setSearch(
                event.target.value
              )
            }
            placeholder="Room, guest, note, transaction code..."
            style={inputStyle}
          />

          <select
            value={entryTypeFilter}
            onChange={(event) =>
              setEntryTypeFilter(
                event.target.value
              )
            }
            style={inputStyle}
          >
            <option value="all">
              All Entries
            </option>

            <option value="revenue">
              Revenue
            </option>

            <option value="payment">
              Payments
            </option>

            <option value="advance">
              Advance / Extra Receipts
            </option>

            <option value="expense">
              Expenses
            </option>

            <option value="cash_handover">
              Cash Handover
            </option>

            <option value="manual_register">
              Manual Rows
            </option>
          </select>

          <select
            value={paymentFilter}
            onChange={(event) =>
              setPaymentFilter(
                event.target.value
              )
            }
            style={inputStyle}
          >
            <option value="all">
              All Payment Modes
            </option>

            <option value="cash">
              Cash
            </option>

            <option value="online">
              All Online
            </option>

            <option value="upi">
              UPI
            </option>

            <option value="card">
              Card
            </option>

            <option value="bank">
              Bank
            </option>

            <option value="fab">
              Fab
            </option>

            <option value="brevistay">
              Brevistay
            </option>

            <option value="ota">
              Other OTA
            </option>
          </select>
        </div>

        <div style={registerInfoBarStyle}>
          <span>
            {filteredEntries.length} of{" "}
            {entries.length} rows
          </span>

          <span>
            Business Date:{" "}
            <strong>
              {businessDate}
            </strong>
          </span>

          <span>
            Auto entries + manual corrections
          </span>
        </div>

        <div style={registerTableWrapperStyle}>
          <table style={registerTableStyle}>
            <thead>
              <tr>
                <RegisterTh>
                  Sr.
                </RegisterTh>

                <RegisterTh>
                  Room
                </RegisterTh>

                <RegisterTh right>
                  Tariff
                </RegisterTh>

                <RegisterTh right>
                  Cash
                </RegisterTh>

                <RegisterTh right>
                  Online
                </RegisterTh>

                <RegisterTh right>
                  FAB
                </RegisterTh>

                <RegisterTh right>
                  Brevistay
                </RegisterTh>

                <RegisterTh>
                  IN
                </RegisterTh>

                <RegisterTh>
                  OUT
                </RegisterTh>

                <RegisterTh right>
                  Balance
                </RegisterTh>

                <RegisterTh>
                  Name
                </RegisterTh>

                <RegisterTh>
                  Remarks / Code
                </RegisterTh>

                <RegisterTh>
                  Action
                </RegisterTh>
              </tr>
            </thead>

            <tbody>
              {manualRowOpen && (
                <EditableRegisterRow
                  row={manualRegisterRow}
                  onChange={
                    updateManualRegisterField
                  }
                  saving={saving}
                  isNew
                  onSave={
                    addManualRegisterRow
                  }
                  onCancel={() => {
                    setManualRowOpen(
                      false
                    );

                    setManualRegisterRow(
                      createEmptyRegisterRow()
                    );
                  }}
                />
              )}

              {loading ? (
                <tr>
                  <td
                    colSpan={13}
                    style={registerEmptyCellStyle}
                  >
                    Day Book register loading...
                  </td>
                </tr>
              ) : filteredEntries.length ===
                0 ? (
                <tr>
                  <td
                    colSpan={13}
                    style={registerEmptyCellStyle}
                  >
                    Is date/filter ke liye koi register row nahi mili.
                  </td>
                </tr>
              ) : (
                filteredEntries.map(
                  (entry, index) => {
                    const row =
                      getRegisterRow(
                        entry,
                        index
                      );

                    const isEditing =
                      editingEntryId ===
                      entry.id;

                    if (isEditing) {
                      return (
                        <EditableRegisterRow
                          key={entry.id}
                          row={
                            editRegisterRow
                          }
                          onChange={
                            updateEditRegisterField
                          }
                          saving={
                            saving
                          }
                          onSave={() =>
                            saveRegisterEdit(
                              entry.id
                            )
                          }
                          onCancel={
                            cancelRegisterEdit
                          }
                        />
                      );
                    }

                    return (
                      <tr
                        key={entry.id}
                        style={
                          registerBodyRowStyle
                        }
                      >
                        <RegisterTd>
                          {row.serial ||
                            index + 1}
                        </RegisterTd>

                        <RegisterTd strong>
                          {row.room ||
                            "-"}
                        </RegisterTd>

                        <RegisterMoneyTd
                          value={
                            row.tariff
                          }
                        />

                        <RegisterMoneyTd
                          value={row.cash}
                          displayText={row.cashLabel}
                        />

                        <RegisterMoneyTd
                          value={row.online}
                          displayText={row.onlineLabel}
                          highlighted={
                            row.online > 0 ||
                            Boolean(row.onlineLabel)
                          }
                          subText={row.transactionReference}
                        />

                        <RegisterMoneyTd
                          value={row.fab}
                          displayText={row.fabLabel}
                        />

                        <RegisterMoneyTd
                          value={row.brevistay}
                          displayText={row.brevistayLabel}
                        />

                        <RegisterTd>
                          {row.checkIn ||
                            "-"}
                        </RegisterTd>

                        <RegisterTd>
                          {row.checkOut ||
                            "-"}
                        </RegisterTd>

                        <RegisterMoneyTd
                          value={
                            row.balance
                          }
                          danger={
                            row.balance > 0
                          }
                        />

                        <RegisterTd strong>
                          {row.guestName ||
                            "-"}
                        </RegisterTd>

                        <RegisterTd>
                          <div style={remarkCellStyle}>
                            {row.note ||
                              "-"}

                            {row.transactionReference &&
                              !row.online && (
                                <small style={codeTextStyle}>
                                  Code:{" "}
                                  {
                                    row.transactionReference
                                  }
                                </small>
                              )}
                          </div>
                        </RegisterTd>

                        <RegisterTd>
                          <button
                            type="button"
                            disabled={
                              saving
                            }
                            onClick={() =>
                              startRegisterEdit(
                                entry,
                                index
                              )
                            }
                            style={
                              editRowButtonStyle
                            }
                          >
                            ✏ Edit
                          </button>
                        </RegisterTd>
                      </tr>
                    );
                  }
                )
              )}
            </tbody>

            <tfoot>
              <tr style={registerTotalRowStyle}>
                <td style={registerTotalLabelStyle}>
                  TOTAL
                </td>

                <td style={registerTotalCellStyle}>
                  -
                </td>

                <RegisterTotalMoney
                  value={
                    registerTotals.tariff
                  }
                />

                <RegisterTotalMoney
                  value={
                    registerTotals.cash
                  }
                />

                <RegisterTotalMoney
                  value={
                    registerTotals.online
                  }
                />

                <RegisterTotalMoney
                  value={
                    registerTotals.fab
                  }
                />

                <RegisterTotalMoney
                  value={
                    registerTotals.brevistay
                  }
                />

                <td style={registerTotalCellStyle}>
                  -
                </td>

                <td style={registerTotalCellStyle}>
                  -
                </td>

                <RegisterTotalMoney
                  value={
                    registerTotals.balance
                  }
                />

                <td style={registerTotalCellStyle}>
                  -
                </td>

                <td style={registerTotalCellStyle}>
                  -
                </td>

                <td style={registerTotalCellStyle}>
                  -
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div style={registerLegendStyle}>
          <span>
            🟨 Highlight = online / separate receipt
          </span>

          <span>
            Auto-generated entry ko edit karne par original transaction delete nahi hota; sirf register display correction save hoti hai.
          </span>
        </div>
      </section>

      <section style={sideLedgerGridStyle}>
        <SideReceiptLedger
          title="🍴 Food Bill Received"
          subtitle="Sirf actual received food payments • order hone par nahi"
          rows={foodReceiptLedger}
          summary={foodReceiptSummary}
          emptyText="Aaj koi Food Bill payment receive nahi hui."
          type="food"
        />

        <ManualAdvancePendingLedger
          rows={
            advancePendingLedger
          }
          summary={
            advancePendingSummary
          }
          formOpen={
            advanceManualFormOpen
          }
          saving={
            advanceManualSaving
          }
          editId={
            advanceManualEditId
          }
          form={
            advanceManualForm
          }
          onToggleForm={() => {
            if (
              advanceManualFormOpen
            ) {
              resetAdvanceManualForm();
            }

            setAdvanceManualFormOpen(
              (current) =>
                !current
            );
          }}
          onFieldChange={
            updateAdvanceManualField
          }
          onSave={
            saveManualAdvancePending
          }
          onEdit={
            editManualAdvancePending
          }
          onDelete={
            deleteManualAdvancePending
          }
          onCancel={() => {
            resetAdvanceManualForm();
            setAdvanceManualFormOpen(
              false
            );
          }}
        />

        <SideReceiptLedger
          title="🧾 Expense"
          subtitle="Daily expenses register se alag yahan show honge"
          rows={
            expenseReceiptLedger
          }
          summary={{
            cash: 0,
            online: 0,
            total:
              expenseReceiptSummary.total,
          }}
          emptyText="Aaj koi Expense entry nahi hai."
          type="expense"
        />
      </section>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Small Components                               */
/* -------------------------------------------------------------------------- */

function ManualAdvancePendingLedger({
  rows,
  summary,
  formOpen,
  saving,
  editId,
  form,
  onToggleForm,
  onFieldChange,
  onSave,
  onEdit,
  onDelete,
  onCancel,
}) {
  return (
    <section style={sideLedgerCardStyle}>
      <div style={sideLedgerHeaderStyle}>
        <div>
          <h4 style={sideLedgerTitleStyle}>
            💰 Advance & Pending Received
          </h4>

          <div style={sideLedgerSubtitleStyle}>
            Pending payment, Check-In extra aur Booking Diary advance automatic yahan aayega • Manual entry bhi add kar sakte ho
          </div>
        </div>

        <div style={manualLedgerHeaderActionStyle}>
          <div style={sideLedgerTotalBadgeStyle}>
            Total{" "}
            {formatMoney(
              summary?.total || 0
            )}
          </div>

          <button
            type="button"
            disabled={saving}
            onClick={onToggleForm}
            style={manualLedgerAddButtonStyle}
          >
            {formOpen
              ? "✕ Close"
              : "＋ Add Entry"}
          </button>
        </div>
      </div>

      {formOpen && (
        <div style={manualLedgerFormStyle}>
          <div style={manualLedgerFormGridStyle}>
            <label>
              <div style={manualLedgerLabelStyle}>
                Amount ₹ *
              </div>

              <input
                type="number"
                min="0"
                value={form.amount}
                disabled={saving}
                onChange={(event) =>
                  onFieldChange(
                    "amount",
                    event.target.value
                  )
                }
                style={manualLedgerInputStyle}
              />
            </label>

            <label>
              <div style={manualLedgerLabelStyle}>
                Room No. *
              </div>

              <input
                value={form.room}
                disabled={saving}
                onChange={(event) =>
                  onFieldChange(
                    "room",
                    event.target.value.toUpperCase()
                  )
                }
                style={manualLedgerInputStyle}
                placeholder="B3"
              />
            </label>

            <label>
              <div style={manualLedgerLabelStyle}>
                Type
              </div>

              <select
                value={form.receiptType}
                disabled={saving}
                onChange={(event) =>
                  onFieldChange(
                    "receiptType",
                    event.target.value
                  )
                }
                style={manualLedgerInputStyle}
              >
                <option>
                  Advance Received
                </option>
                <option>
                  Pending Amount Received
                </option>
                <option>
                  Early Check-In Charge Received
                </option>
                <option>
                  Late Checkout Charge Received
                </option>
                <option>
                  Extra Mattress Charge Received
                </option>
                <option>
                  Other Receipt
                </option>
              </select>
            </label>

            <label>
              <div style={manualLedgerLabelStyle}>
                Payment Mode
              </div>

              <select
                value={form.paymentMode}
                disabled={saving}
                onChange={(event) =>
                  onFieldChange(
                    "paymentMode",
                    event.target.value
                  )
                }
                style={manualLedgerInputStyle}
              >
                <option>
                  Cash
                </option>
                <option>
                  UPI
                </option>
                <option>
                  Card
                </option>
                <option>
                  Bank Transfer
                </option>
              </select>
            </label>

            {form.paymentMode !== "Cash" && (
              <label>
                <div style={manualLedgerLabelStyle}>
                  Transaction Code *
                </div>

                <input
                  value={
                    form.transactionReference
                  }
                  disabled={saving}
                  onChange={(event) =>
                    onFieldChange(
                      "transactionReference",
                      event.target.value
                    )
                  }
                  style={manualLedgerInputStyle}
                  placeholder="6008"
                />
              </label>
            )}

            <label>
              <div style={manualLedgerLabelStyle}>
                Time
              </div>

              <input
                type="time"
                value={form.time}
                disabled={saving}
                onChange={(event) =>
                  onFieldChange(
                    "time",
                    event.target.value
                  )
                }
                style={manualLedgerInputStyle}
              />
            </label>

            <label>
              <div style={manualLedgerLabelStyle}>
                Guest Name
              </div>

              <input
                value={form.guestName}
                disabled={saving}
                onChange={(event) =>
                  onFieldChange(
                    "guestName",
                    event.target.value
                  )
                }
                style={manualLedgerInputStyle}
              />
            </label>

            <label style={manualLedgerWideFieldStyle}>
              <div style={manualLedgerLabelStyle}>
                Note / Description
              </div>

              <input
                value={form.note}
                disabled={saving}
                onChange={(event) =>
                  onFieldChange(
                    "note",
                    event.target.value
                  )
                }
                style={manualLedgerInputStyle}
                placeholder="Adv Amt Rec Room No B3 / Pending amount received..."
              />
            </label>
          </div>

          <div style={manualLedgerFormButtonsStyle}>
            <button
              type="button"
              disabled={saving}
              onClick={onSave}
              style={manualLedgerSaveButtonStyle}
            >
              {saving
                ? "Saving..."
                : editId
                ? "✓ Update Entry"
                : "＋ Save Entry"}
            </button>

            <button
              type="button"
              disabled={saving}
              onClick={onCancel}
              style={manualLedgerCancelButtonStyle}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={sideLedgerBodyStyle}>
        {rows.length === 0 ? (
          <div style={sideLedgerEmptyStyle}>
            Aaj koi Advance / Pending receipt nahi hai.
          </div>
        ) : (
          rows.map(
            (row, index) => (
              <div
                key={
                  row.id ||
                  index
                }
                style={{
                  ...sideLedgerEntryStyle,
                  background:
                    getPaymentModeKey(
                      row.mode
                    ) === "cash"
                      ? "#ffffff"
                      : "#fef9c3",
                }}
              >
                <div style={sideLedgerEntryTopStyle}>
                  <div>
                    <div style={sideLedgerMainLineStyle}>
                      <span style={sideLedgerAmountStyle}>
                        {formatMoney(
                          row.amount
                        )}
                      </span>

                      <span>
                        {row.note ||
                          `Adv / Pending received • Room ${row.room}`}
                      </span>
                    </div>

                    <div style={sideLedgerMetaStyle}>
                      <span>
                        {row.time || "-"}
                      </span>

                      <span>
                        {row.mode || "Cash"}
                      </span>

                      {row.code && (
                        <span style={sideLedgerCodeStyle}>
                          [{row.code}]
                        </span>
                      )}
                    </div>

                    {row.guestName && (
                      <div style={sideLedgerGuestStyle}>
                        Guest: {row.guestName}
                      </div>
                    )}
                  </div>

                  {![
                    "checkin_excess_received",
                    "booking_diary_advance",
                    "pending_received",
                  ].includes(
                    row.category
                  ) ? (
                    <div style={manualLedgerRowButtonsStyle}>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() =>
                          onEdit(
                            row.originalEntry ||
                              row
                          )
                        }
                        style={manualLedgerEditButtonStyle}
                      >
                        ✏ Edit
                      </button>

                      <button
                        type="button"
                        disabled={saving}
                        onClick={() =>
                          onDelete(
                            row.originalEntry ||
                              row
                          )
                        }
                        style={manualLedgerDeleteButtonStyle}
                      >
                        🗑
                      </button>
                    </div>
                  ) : (
                    <span style={autoEntryBadgeStyle}>
                      Auto
                    </span>
                  )}
                </div>
              </div>
            )
          )
        )}
      </div>

      <div style={sideLedgerSummaryStyle}>
        <div style={sideLedgerSummaryCellStyle}>
          <span style={sideLedgerSummaryLabelStyle}>
            Cash
          </span>
          <strong style={sideLedgerSummaryValueStyle}>
            {formatMoney(
              summary?.cash || 0
            )}
          </strong>
        </div>

        <div style={sideLedgerSummaryCellStyle}>
          <span style={sideLedgerSummaryLabelStyle}>
            Online
          </span>
          <strong style={sideLedgerSummaryValueStyle}>
            {formatMoney(
              summary?.online || 0
            )}
          </strong>
        </div>

        <div style={sideLedgerSummaryCellStyle}>
          <span style={sideLedgerSummaryLabelStyle}>
            Total
          </span>
          <strong style={sideLedgerSummaryValueStyle}>
            {formatMoney(
              summary?.total || 0
            )}
          </strong>
        </div>
      </div>
    </section>
  );
}

function SideReceiptLedger({
  title,
  subtitle,
  rows,
  summary,
  emptyText,
  type,
}) {
  return (
    <section style={sideLedgerCardStyle}>
      <div style={sideLedgerHeaderStyle}>
        <div>
          <h4 style={sideLedgerTitleStyle}>
            {title}
          </h4>

          <div style={sideLedgerSubtitleStyle}>
            {subtitle}
          </div>
        </div>

        <div style={sideLedgerTotalBadgeStyle}>
          Total{" "}
          {formatMoney(
            summary?.total || 0
          )}
        </div>
      </div>

      <div style={sideLedgerBodyStyle}>
        {rows.length === 0 ? (
          <div style={sideLedgerEmptyStyle}>
            {emptyText}
          </div>
        ) : (
          rows.map(
            (row, index) => {
              const isOnline =
                getPaymentModeKey(
                  row.mode
                ) !== "cash";

              return (
                <div
                  key={
                    row.id ||
                    index
                  }
                  style={{
                    ...sideLedgerEntryStyle,

                    background:
                      isOnline
                        ? "#fef9c3"
                        : "#ffffff",
                  }}
                >
                  <div style={sideLedgerMainLineStyle}>
                    <span style={sideLedgerAmountStyle}>
                      {formatMoney(
                        row.amount
                      )}
                    </span>

                    <span>
                      {type ===
                      "food"
                        ? `received from ${row.room}`
                        : `Adv / Pending received • Room ${row.room}`}
                    </span>
                  </div>

                  <div style={sideLedgerMetaStyle}>
                    <span>
                      {row.time || "-"}
                    </span>

                    <span>
                      {row.mode || "Cash"}
                    </span>

                    {row.code && (
                      <span style={sideLedgerCodeStyle}>
                        [{row.code}]
                      </span>
                    )}
                  </div>

                  {row.guestName && (
                    <div style={sideLedgerGuestStyle}>
                      Guest: {row.guestName}
                    </div>
                  )}

                  {row.note && (
                    <div style={sideLedgerNoteStyle}>
                      {row.note}
                    </div>
                  )}
                </div>
              );
            }
          )
        )}
      </div>

      <div style={sideLedgerSummaryStyle}>
        <div style={sideLedgerSummaryCellStyle}>
          <span style={sideLedgerSummaryLabelStyle}>Cash</span>
          <strong style={sideLedgerSummaryValueStyle}>
            {formatMoney(
              summary?.cash || 0
            )}
          </strong>
        </div>

        <div style={sideLedgerSummaryCellStyle}>
          <span style={sideLedgerSummaryLabelStyle}>Online</span>
          <strong style={sideLedgerSummaryValueStyle}>
            {formatMoney(
              summary?.online || 0
            )}
          </strong>
        </div>

        <div style={sideLedgerSummaryCellStyle}>
          <span style={sideLedgerSummaryLabelStyle}>Total</span>
          <strong style={sideLedgerSummaryValueStyle}>
            {formatMoney(
              summary?.total || 0
            )}
          </strong>
        </div>
      </div>
    </section>
  );
}

function RegisterTh({
  children,
  right = false,
}) {
  return (
    <th
      style={{
        ...registerThStyle,

        textAlign: right
          ? "right"
          : "left",
      }}
    >
      {children}
    </th>
  );
}

function RegisterTd({
  children,
  strong = false,
}) {
  return (
    <td
      style={{
        ...registerTdStyle,

        fontWeight: strong
          ? 800
          : 500,
      }}
    >
      {children}
    </td>
  );
}

function RegisterMoneyTd({
  value,
  highlighted = false,
  danger = false,
  subText = "",
  displayText = "",
}) {
  const amount =
    Math.max(
      0,
      toAmount(value)
    );

  return (
    <td
      style={{
        ...registerMoneyTdStyle,

        background:
          highlighted
            ? "#fef9c3"
            : "#ffffff",

        color:
          danger && amount > 0
            ? "#b91c1c"
            : "#111827",
      }}
    >
      {clean(displayText) ||
        (amount > 0
          ? formatMoney(amount)
          : "-")}

      {subText && (
        <div style={registerSubTextStyle}>
          ({subText})
        </div>
      )}
    </td>
  );
}

function RegisterTotalMoney({
  value,
}) {
  return (
    <td
      style={{
        ...registerTotalCellStyle,

        textAlign: "right",

        fontWeight: 900,
      }}
    >
      {toAmount(value)
        ? formatMoney(value)
        : "-"}
    </td>
  );
}

function RegisterInput({
  value,
  onChange,
  type = "text",
  right = false,
  placeholder = "",
  min,
}) {
  return (
    <input
      type={type}
      min={min}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(event) =>
        onChange(
          event.target.value
        )
      }
      style={{
        ...registerCellInputStyle,

        textAlign: right
          ? "right"
          : "left",
      }}
    />
  );
}

function EditableRegisterRow({
  row,
  onChange,
  onSave,
  onCancel,
  saving,
  isNew = false,
}) {
  return (
    <tr style={editableRegisterRowStyle}>
      <td style={registerEditTdStyle}>
        <RegisterInput
          value={row.serial}
          onChange={(value) =>
            onChange(
              "serial",
              value
            )
          }
          placeholder="Sr."
        />
      </td>

      <td style={registerEditTdStyle}>
        <RegisterInput
          value={row.room}
          onChange={(value) =>
            onChange(
              "room",
              value.toUpperCase()
            )
          }
          placeholder="A1"
        />
      </td>

      <td style={registerEditTdStyle}>
        <RegisterInput
          type="number"
          min="0"
          right
          value={row.tariff}
          onChange={(value) =>
            onChange(
              "tariff",
              value
            )
          }
        />
      </td>

      <td style={registerEditTdStyle}>
        <RegisterInput
          type="number"
          min="0"
          right
          value={row.cash}
          onChange={(value) =>
            onChange(
              "cash",
              value
            )
          }
        />
      </td>

      <td style={registerEditTdStyle}>
        <RegisterInput
          type="number"
          min="0"
          right
          value={row.online}
          onChange={(value) =>
            onChange(
              "online",
              value
            )
          }
        />

        <RegisterInput
          value={
            row.transactionReference
          }
          onChange={(value) =>
            onChange(
              "transactionReference",
              value
            )
          }
          placeholder="Code"
        />
      </td>

      <td style={registerEditTdStyle}>
        <RegisterInput
          type="number"
          min="0"
          right
          value={row.fab}
          onChange={(value) =>
            onChange(
              "fab",
              value
            )
          }
        />
      </td>

      <td style={registerEditTdStyle}>
        <RegisterInput
          type="number"
          min="0"
          right
          value={row.brevistay}
          onChange={(value) =>
            onChange(
              "brevistay",
              value
            )
          }
        />
      </td>

      <td style={registerEditTdStyle}>
        <RegisterInput
          value={row.checkIn}
          onChange={(value) =>
            onChange(
              "checkIn",
              value
            )
          }
          placeholder="10:30 AM"
        />
      </td>

      <td style={registerEditTdStyle}>
        <RegisterInput
          value={row.checkOut}
          onChange={(value) =>
            onChange(
              "checkOut",
              value
            )
          }
          placeholder="11:00 AM"
        />
      </td>

      <td style={registerEditTdStyle}>
        <RegisterInput
          type="number"
          min="0"
          right
          value={row.balance}
          onChange={(value) =>
            onChange(
              "balance",
              value
            )
          }
        />
      </td>

      <td style={registerEditTdStyle}>
        <RegisterInput
          value={row.guestName}
          onChange={(value) =>
            onChange(
              "guestName",
              value
            )
          }
          placeholder="Guest"
        />
      </td>

      <td style={registerEditTdStyle}>
        <textarea
          value={row.note ?? ""}
          onChange={(event) =>
            onChange(
              "note",
              event.target.value
            )
          }
          placeholder="Remarks"
          style={registerCellTextareaStyle}
        />
      </td>

      <td style={registerEditTdStyle}>
        <div style={editActionsStyle}>
          <button
            type="button"
            disabled={saving}
            onClick={onSave}
            style={saveRowButtonStyle}
          >
            {saving
              ? "..."
              : isNew
              ? "＋ Add"
              : "✓ Save"}
          </button>

          <button
            type="button"
            disabled={saving}
            onClick={onCancel}
            style={cancelRowButtonStyle}
          >
            ✕
          </button>
        </div>
      </td>
    </tr>
  );
}

function Field({
  label,
  children,
}) {
  return (
    <label style={fieldStyle}>
      <span style={labelStyle}>
        {label}
      </span>

      {children}
    </label>
  );
}

function SummaryCard({
  label,
  value,
  strong = false,
  danger = false,
  warning = false,
}) {
  return (
    <div style={summaryCardStyle}>
      <div style={summaryLabelStyle}>
        {label}
      </div>

      <div
        style={{
          ...summaryValueStyle,

          fontSize: strong
            ? 22
            : 18,

          color: danger
            ? "#dc2626"
            : warning
            ? "#c2410c"
            : "#0f172a",
        }}
      >
        {formatMoney(value)}
      </div>
    </div>
  );
}

function AmountRow({
  label,
  value,
  strong = false,
  danger = false,
}) {
  return (
    <div
      style={{
        ...amountRowStyle,

        fontWeight: strong
          ? 900
          : 600,

        color: danger
          ? "#dc2626"
          : "#334155",
      }}
    >
      <span>{label}</span>

      <span>
        {formatMoney(value)}
      </span>
    </div>
  );
}

function EntryBadge({ entry }) {
  let text = entry.entryType;
  let background = "#e2e8f0";
  let color = "#475569";

  if (isCashHandoverEntry(entry)) {
    text = "Cash Handover";
    background = "#ffedd5";
    color = "#c2410c";
  } else if (isExpenseEntry(entry)) {
    text = "Expense";
    background = "#fee2e2";
    color = "#b91c1c";
  } else if (isAdvanceEntry(entry)) {
    text = "Advance / Receipt";
    background = "#fef3c7";
    color = "#92400e";
  } else if (isPaymentEntry(entry)) {
    text = "Payment";
    background = "#dcfce7";
    color = "#166534";
  } else if (isRevenueEntry(entry)) {
    text = "Revenue";
    background = "#dbeafe";
    color = "#1d4ed8";
  }

  return (
    <span
      style={{
        ...badgeStyle,
        background,
        color,
      }}
    >
      {text}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const formTabStyle = (active) => ({
  border: 0,
  borderRadius: 9,

  padding: "9px 12px",

  background: active
    ? "#1d4ed8"
    : "#e2e8f0",

  color: active
    ? "#ffffff"
    : "#334155",

  fontWeight: 800,
  cursor: "pointer",
});

const sectionStyle = {
  marginTop: 36,
  padding: 18,

  border:
    "1px solid #e2e8f0",

  borderRadius: 18,

  background: "#ffffff",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 12,
  flexWrap: "wrap",
};

const titleStyle = {
  margin: 0,
  color: "#0f172a",
};

const subtitleStyle = {
  marginTop: 5,

  color: "#64748b",

  fontSize: 12,
};

const headerActionsStyle = {
  display: "flex",
  alignItems: "center",

  gap: 8,
  flexWrap: "wrap",
};

const compactLabelStyle = {
  display: "flex",
  alignItems: "center",

  gap: 6,

  padding: "9px 10px",

  borderRadius: 9,

  background: "#f8fafc",

  border:
    "1px solid #cbd5e1",

  fontSize: 11,
  fontWeight: 700,
};

const summaryGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(135px,1fr))",

  gap: 9,

  marginTop: 16,
};

const summaryCardStyle = {
  padding: 12,

  borderRadius: 12,

  background: "#f8fafc",

  border:
    "1px solid #e2e8f0",

  textAlign: "center",
};

const summaryLabelStyle = {
  color: "#64748b",

  fontSize: 10,
  fontWeight: 800,

  textTransform: "uppercase",
};

const summaryValueStyle = {
  marginTop: 5,

  fontWeight: 900,

  overflowWrap: "anywhere",
};

const cashSectionGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(280px,1fr))",

  gap: 12,

  marginTop: 12,
};

const panelStyle = {
  marginTop: 12,
  padding: 14,

  border:
    "1px solid #e2e8f0",

  borderRadius: 14,

  background: "#f8fafc",
};

const panelTitleStyle = {
  margin: "0 0 11px",

  color: "#0f172a",
};

const amountRowStyle = {
  display: "flex",
  justifyContent: "space-between",

  gap: 12,

  padding: "6px 0",
};

const dividerStyle = {
  height: 1,

  margin: "7px 0",

  background: "#cbd5e1",
};

const openingFormStyle = {
  display: "flex",

  gap: 8,

  marginTop: 10,
};

const sourceTextStyle = {
  marginTop: 7,

  color: "#64748b",

  fontSize: 10,
};

const inputStyle = {
  width: "100%",
  minHeight: 42,

  padding: 10,

  border:
    "1px solid #cbd5e1",

  borderRadius: 9,

  background: "#ffffff",

  boxSizing: "border-box",
};

const primaryButtonStyle = {
  padding: "10px 14px",

  border: 0,
  borderRadius: 9,

  background: "#2563eb",
  color: "#ffffff",

  fontWeight: 800,
  cursor: "pointer",
};

const printButtonStyle = {
  ...primaryButtonStyle,
  background: "#0f766e",
};

const carryButtonStyle = {
  width: "100%",

  marginTop: 10,
  padding: 10,

  border:
    "1px solid #93c5fd",

  borderRadius: 9,

  background: "#eff6ff",
  color: "#1d4ed8",

  fontWeight: 800,
  cursor: "pointer",
};

const formTabsStyle = {
  display: "flex",

  gap: 8,
  flexWrap: "wrap",
};

const formStyle = {
  marginTop: 14,
};

const formGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(190px,1fr))",

  gap: 10,
};

const fieldStyle = {
  display: "block",
};

const labelStyle = {
  display: "block",

  marginBottom: 6,

  color: "#334155",

  fontSize: 11,
  fontWeight: 800,
};

const fullPrimaryButtonStyle = {
  width: "100%",

  marginTop: 11,
  padding: 11,

  border: 0,
  borderRadius: 10,

  background: "#16a34a",
  color: "#ffffff",

  fontWeight: 900,
  cursor: "pointer",
};

const handoverWarningStyle = {
  marginTop: 10,
  padding: 9,

  borderRadius: 9,

  background: "#fff7ed",
  color: "#9a3412",

  fontSize: 11,
  fontWeight: 700,
};

const handoverButtonStyle = {
  ...fullPrimaryButtonStyle,
  background: "#ea580c",
};

const entriesHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 12,
  flexWrap: "wrap",
};

const filtersStyle = {
  display: "grid",

  gridTemplateColumns:
    "minmax(220px,1fr) minmax(140px,190px) minmax(140px,190px)",

  gap: 8,

  flex: 1,
  minWidth: 280,
};

const tableWrapperStyle = {
  marginTop: 12,

  overflowX: "auto",

  borderRadius: 10,

  border:
    "1px solid #cbd5e1",
};

const tableStyle = {
  width: "100%",

  minWidth: 1050,

  borderCollapse: "collapse",

  background: "#ffffff",
};

const thStyle = {
  padding: 9,

  borderBottom:
    "1px solid #cbd5e1",

  background: "#e2e8f0",

  color: "#334155",

  fontSize: 10,
  fontWeight: 900,

  textAlign: "left",

  whiteSpace: "nowrap",
};

const thRightStyle = {
  ...thStyle,
  textAlign: "right",
};

const tdStyle = {
  padding: 9,

  borderBottom:
    "1px solid #e2e8f0",

  color: "#334155",

  fontSize: 10,

  verticalAlign: "top",
};

const tdRightStyle = {
  ...tdStyle,
  textAlign: "right",
};

const tableSubTextStyle = {
  marginTop: 3,

  color: "#64748b",

  fontSize: 9,
};

const badgeStyle = {
  display: "inline-block",

  padding: "4px 7px",

  borderRadius: 999,

  fontSize: 9,
  fontWeight: 900,

  whiteSpace: "nowrap",
};

const emptyBoxStyle = {
  marginTop: 12,
  padding: 16,

  border:
    "1px dashed #cbd5e1",

  borderRadius: 10,

  background: "#ffffff",
  color: "#64748b",

  textAlign: "center",

  fontSize: 12,
};

const errorBoxStyle = {
  marginTop: 12,
  padding: 11,

  borderRadius: 10,

  border:
    "1px solid #fecaca",

  background: "#fef2f2",
  color: "#b91c1c",

  fontSize: 12,
  fontWeight: 700,
};


const manualLedgerHeaderActionStyle = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  flexWrap: "wrap",
  justifyContent: "flex-end",
};

const manualLedgerAddButtonStyle = {
  padding: "7px 9px",
  border: 0,
  borderRadius: 7,
  background: "#2563eb",
  color: "#ffffff",
  fontSize: 9,
  fontWeight: 900,
  cursor: "pointer",
};

const manualLedgerFormStyle = {
  padding: 11,
  borderBottom:
    "1px solid #cbd5e1",
  background: "#eff6ff",
};

const manualLedgerFormGridStyle = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit,minmax(145px,1fr))",
  gap: 8,
};

const manualLedgerLabelStyle = {
  marginBottom: 4,
  color: "#334155",
  fontSize: 8,
  fontWeight: 900,
};

const manualLedgerInputStyle = {
  width: "100%",
  padding: "7px 8px",
  border:
    "1px solid #94a3b8",
  borderRadius: 6,
  background: "#ffffff",
  color: "#111827",
  fontSize: 9,
  boxSizing: "border-box",
};

const manualLedgerWideFieldStyle = {
  gridColumn:
    "1 / -1",
};

const manualLedgerFormButtonsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 7,
  marginTop: 9,
};

const manualLedgerSaveButtonStyle = {
  padding: "8px 11px",
  border: 0,
  borderRadius: 7,
  background: "#16a34a",
  color: "#ffffff",
  fontSize: 9,
  fontWeight: 900,
  cursor: "pointer",
};

const manualLedgerCancelButtonStyle = {
  padding: "8px 11px",
  border:
    "1px solid #cbd5e1",
  borderRadius: 7,
  background: "#ffffff",
  color: "#475569",
  fontSize: 9,
  fontWeight: 900,
  cursor: "pointer",
};

const sideLedgerEntryTopStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 8,
};

const autoEntryBadgeStyle = {
  padding: "5px 7px",
  borderRadius: 999,
  background: "#dcfce7",
  color: "#166534",
  fontSize: 8,
  fontWeight: 900,
  whiteSpace: "nowrap",
};

const manualLedgerRowButtonsStyle = {
  display: "flex",
  gap: 5,
  flexShrink: 0,
};

const manualLedgerEditButtonStyle = {
  padding: "5px 7px",
  border:
    "1px solid #93c5fd",
  borderRadius: 6,
  background: "#eff6ff",
  color: "#1d4ed8",
  fontSize: 8,
  fontWeight: 900,
  cursor: "pointer",
};

const manualLedgerDeleteButtonStyle = {
  padding: "5px 7px",
  border:
    "1px solid #fecaca",
  borderRadius: 6,
  background: "#fef2f2",
  color: "#b91c1c",
  fontSize: 8,
  fontWeight: 900,
  cursor: "pointer",
};

const sideLedgerGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(360px,1fr))",

  gap: 14,

  marginTop: 14,
};

const sideLedgerCardStyle = {
  border:
    "1px solid #94a3b8",

  borderRadius: 12,

  background: "#ffffff",

  overflow: "hidden",
};

const sideLedgerHeaderStyle = {
  display: "flex",

  justifyContent:
    "space-between",

  alignItems: "flex-start",

  gap: 10,

  padding: 12,

  borderBottom:
    "1px solid #cbd5e1",

  background: "#f8fafc",
};

const sideLedgerTitleStyle = {
  margin: 0,

  color: "#0f172a",

  fontSize: 14,
  fontWeight: 900,
};

const sideLedgerSubtitleStyle = {
  marginTop: 4,

  color: "#64748b",

  fontSize: 9,
};

const sideLedgerTotalBadgeStyle = {
  padding: "6px 9px",

  borderRadius: 999,

  background: "#e2e8f0",

  color: "#0f172a",

  fontSize: 10,
  fontWeight: 900,

  whiteSpace: "nowrap",
};

const sideLedgerBodyStyle = {
  display: "grid",

  gap: 0,

  minHeight: 120,

  maxHeight: 360,

  overflowY: "auto",
};

const sideLedgerEntryStyle = {
  padding: "10px 12px",

  borderBottom:
    "1px solid #e2e8f0",
};

const sideLedgerMainLineStyle = {
  display: "flex",

  alignItems: "baseline",

  gap: 7,

  flexWrap: "wrap",

  color: "#111827",

  fontSize: 11,
  fontWeight: 700,
};

const sideLedgerAmountStyle = {
  color: "#111827",

  fontSize: 13,
  fontWeight: 900,
};

const sideLedgerMetaStyle = {
  display: "flex",

  gap: 8,

  flexWrap: "wrap",

  marginTop: 4,

  color: "#64748b",

  fontSize: 9,
  fontWeight: 700,
};

const sideLedgerCodeStyle = {
  color: "#7c3aed",

  fontWeight: 900,
};

const sideLedgerGuestStyle = {
  marginTop: 4,

  color: "#334155",

  fontSize: 9,
};

const sideLedgerNoteStyle = {
  marginTop: 4,

  color: "#475569",

  fontSize: 9,
  fontStyle: "italic",
};

const sideLedgerEmptyStyle = {
  display: "flex",

  alignItems: "center",

  justifyContent: "center",

  minHeight: 120,

  padding: 20,

  color: "#94a3b8",

  fontSize: 10,
  textAlign: "center",
};

const sideLedgerSummaryStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(3,1fr)",

  gap: 0,

  borderTop:
    "1px solid #94a3b8",

  background: "#e2e8f0",
};


const sideLedgerSummaryCellStyle = {
  display: "grid",

  gap: 3,

  padding: 9,

  borderRight:
    "1px solid #94a3b8",

  textAlign: "center",
};

const sideLedgerSummaryLabelStyle = {
  color: "#64748b",

  fontSize: 8,
  fontWeight: 800,
};

const sideLedgerSummaryValueStyle = {
  color: "#0f172a",

  fontSize: 11,
  fontWeight: 900,
};

const registerPanelStyle = {
  marginTop: 14,
  padding: 12,

  border:
    "1px solid #94a3b8",

  borderRadius: 12,

  background: "#ffffff",
};

const registerHeaderButtonsStyle = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
};

const addRowButtonStyle = {
  padding: "10px 13px",

  border: 0,
  borderRadius: 8,

  background: "#1d4ed8",
  color: "#ffffff",

  fontWeight: 900,
  cursor: "pointer",
};

const registerInfoBarStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",

  marginTop: 10,
  padding: "7px 9px",

  border:
    "1px solid #e2e8f0",

  background: "#f8fafc",

  color: "#475569",

  fontSize: 10,
};

const registerTableWrapperStyle = {
  marginTop: 10,

  overflowX: "auto",

  border:
    "1px solid #64748b",

  borderRadius: 4,

  background: "#ffffff",
};

const registerTableStyle = {
  width: "100%",

  minWidth: 1900,

  borderCollapse: "collapse",

  tableLayout: "fixed",

  background: "#ffffff",
};

const registerThStyle = {
  padding: "7px 6px",

  border:
    "1px solid #64748b",

  background: "#e5e7eb",

  color: "#111827",

  fontSize: 9,
  fontWeight: 900,

  whiteSpace: "nowrap",

  position: "sticky",
  top: 0,
  zIndex: 2,
};

const registerTdStyle = {
  padding: "6px 6px",

  border:
    "1px solid #94a3b8",

  color: "#111827",

  fontSize: 9,

  verticalAlign: "top",

  background: "#ffffff",

  overflowWrap: "anywhere",
};

const registerMoneyTdStyle = {
  ...registerTdStyle,

  textAlign: "right",

  fontVariantNumeric:
    "tabular-nums",

  fontWeight: 700,
};

const registerBodyRowStyle = {
  background: "#ffffff",
};

const registerSubTextStyle = {
  marginTop: 3,

  color: "#7c3aed",

  fontSize: 8,
  fontWeight: 800,
};

const remarkCellStyle = {
  display: "grid",
  gap: 3,
};

const codeTextStyle = {
  color: "#7c3aed",

  fontSize: 8,
  fontWeight: 800,
};

const editRowButtonStyle = {
  minWidth: 62,

  padding: "6px 8px",

  border:
    "1px solid #93c5fd",

  borderRadius: 6,

  background: "#eff6ff",
  color: "#1d4ed8",

  fontSize: 9,
  fontWeight: 900,

  cursor: "pointer",
};

const editableRegisterRowStyle = {
  background: "#fffbeb",
};

const registerEditTdStyle = {
  padding: 3,

  border:
    "1px solid #64748b",

  verticalAlign: "top",

  background: "#fffbeb",
};

const registerCellInputStyle = {
  width: "100%",

  minWidth: 55,

  padding: "6px 5px",

  border:
    "1px solid #cbd5e1",

  borderRadius: 4,

  background: "#ffffff",

  color: "#111827",

  fontSize: 9,

  boxSizing: "border-box",

  outline: "none",
};

const registerCellTextareaStyle = {
  ...registerCellInputStyle,

  minHeight: 52,

  resize: "vertical",
};

const editActionsStyle = {
  display: "grid",
  gap: 5,
};

const saveRowButtonStyle = {
  padding: "6px 7px",

  border: 0,
  borderRadius: 5,

  background: "#16a34a",
  color: "#ffffff",

  fontSize: 9,
  fontWeight: 900,

  cursor: "pointer",
};

const cancelRowButtonStyle = {
  padding: "6px 7px",

  border:
    "1px solid #cbd5e1",

  borderRadius: 5,

  background: "#ffffff",
  color: "#475569",

  fontSize: 9,
  fontWeight: 900,

  cursor: "pointer",
};

const registerTotalRowStyle = {
  background: "#e2e8f0",
};

const registerTotalLabelStyle = {
  padding: 7,

  border:
    "1px solid #64748b",

  background: "#cbd5e1",

  color: "#0f172a",

  fontSize: 9,
  fontWeight: 900,
};

const registerTotalCellStyle = {
  padding: 7,

  border:
    "1px solid #64748b",

  background: "#e2e8f0",

  color: "#0f172a",

  fontSize: 9,

  fontVariantNumeric:
    "tabular-nums",
};

const registerEmptyCellStyle = {
  padding: 20,

  border:
    "1px solid #94a3b8",

  color: "#64748b",

  textAlign: "center",

  fontSize: 11,
};

const registerLegendStyle = {
  display: "flex",
  justifyContent: "space-between",

  gap: 12,
  flexWrap: "wrap",

  marginTop: 8,

  color: "#64748b",

  fontSize: 9,
};


export default DayBookPanel;